import { zoneDisplayPoints, nearestZoneVertex, zoneInsertionIndex } from './zoneEditing';
import NextBranchPlayButton from './NextBranchPlayButton';
import { gaitPlaybackDirection, playerTravelSpeed } from './playerMotion';
import CaptureControl from './CaptureControl';
import MasterPlayButton from './MasterPlayButton';
import { UndoIcon, RedoIcon } from './ControlIcons';
import { createFollowScratch, updateFollowCamera } from './cameraFollow';
import { sampleCharacterAction } from './characterActions';
import { getRuntimeAdapter, isMobileBuild, coarsePointer } from './platform';
/**
 * Viewport3D.js — Main 3D Viewport React Component for KickViz
 * ════════════════════════════════════════════════════════════════════
 *
 * PURPOSE
 *   Renders the interactive 3D football pitch viewport using Three.js.
 *   Manages the WebGL renderer, dual cameras (perspective + orthographic),
 *   OrbitControls, post-processing (bloom), raycasting for selection/placement,
 *   animation playback, and the complete UI control overlay (action buttons,
 *   camera toggles, arc/zone editors, animation controls, screenshot).
 *
 * ROLE IN THE APP
 *   This is the primary visualisation component.  It receives pitch state
 *   (players, ball, cones, targets, zones), team data, and selection
 *   from the parent, and renders the full 3D scene with interactive controls.
 *   All 3D objects are created via factory functions from utilitiesFootball3D.js.
 *
 * KEY SYSTEMS
 *   • Camera system         — perspective (3D) + orthographic (2D top-down)
 *     with smooth animated transitions; RenderPass.camera swapped on toggle
 *   • OrbitControls         — mode switching (orbit/pan); right-click always
 *     orbits in 3D mode; 2D mode uses pan-only with XZ-plane panning
 *   • Raycasting            — pointer-based selection, placement, and dragging
 *     of players, ball, cones, targets, and zones; shadow blobs filtered out;
 *     priority-based selection (players/ball before zones)
 *   • Scene management      — useEffect-driven upsert/remove of 3D objects
 *     on pitchState / teamState changes; proper disposal of
 *     non-cached geometries/materials
 *   • Transition mode       — ghosted (semi-transparent) objects for items
 *     without a next-phase position; transition arrows with animation curves;
 *     ball-follows-player mode (ball.attached field) where the ball inherits
 *     a player's movement curve and destination
 *   • Animation playback    — keyframe model: items move along transition
 *     curves from phase N to N+1; per-item offset range (dual-knob);
 *     per-phase duration; Play All mode advances through phases automatically
 *   • Combo arrows          — shows selected item's transitions across
 *     adjacent phases; 3-segment handoff arrows (green carry, gray handoff,
 *     green carry); respects offset and path shape
 *   • Ball handoff          — 3-zone logic: follow source player before offset,
 *     use ball's own curve during offset, follow destination player after offset
 *   • Manual path editing   — add/move/remove waypoints for custom paths
 *   • Zone editing          — add/move/remove polygon vertices
 *   • Post-processing       — EffectComposer with UnrealBloomPass + OutputPass
 *   • Mobile UI             — collapsible left/right control panels
 *
 * RELATIONSHIPS
 *   • Imports all 3D factory functions from utilitiesFootball3D.js
 *   • Uses three/examples OrbitControls, EffectComposer, RenderPass,
 *     UnrealBloomPass, OutputPass
 *   • Sky texture loaded from public/sky.jpg (equirectangular)
 *
 * EXPORTED
 *   Viewport3D (default export) — React component
 */

import * as THREE from 'three';
import { firstItemHit, canTapPlacePlayer, itemDescriptor, itemReference, createViewportPicker, PointerGesture, bindViewportPointers } from './viewportInteraction';

import {useRef, useState, useEffect, useMemo, useCallback} from 'react';
import {
    createFootballPitch,
    createPlayerObject,
    preloadPlayerModel,
    preloadCharacterAssets,
    preloadTeamTextures,
    collectNeededStyles,
    isCharacterMeshReady,
    updatePlayerAnimation,
    preloadNumberTextures,
    getPlayerObjectName,
    createBallObject,
    preloadBallModel,
    createConeObject,
    createTargetObject,
    createZoneObject,
    PLAYER_LABEL_CANVAS_H,
    PLAYER_LABEL_FONT_PX,
} from "./utilitiesFootball3D";
import { ballIsVisible, findSegment, interpolateWithSegment } from "./data";
// Attachment-aware sampling lives in its own module (no three.js in it) so the
// attach / pass / detach interpolation can be tested directly.
import { sampleEffectivePosition, sampleTrackPosition, isPlayerTrackId, markOffset, MARK_DEFAULT_DIST, hasAttachmentFlight } from './sampling';
import { useI18n } from './i18n';
import { playerFacingYaw, smoothPlayerYaw } from './playerFacing';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';

// ═══════════════════════════════════════════════════════════════
//  CONSTANTS & MODULE-LEVEL HELPERS
// ═══════════════════════════════════════════════════════════════

/** Opacity used when ghosting items (no next-phase position in transition mode). */
const GHOST_OPACITY = 0.35;
/** Pixel distance a pointer must travel before being treated as a drag (not a click). */
const DRAG_THRESHOLD = 8;
/**
 * Metres of pitch a press has to land within to mean "this corner" while the
 * editor is in + Add mode. A thumb covers roughly this much of the pitch, so
 * closer than this the finger is on the vertex and the intent is not a guess;
 * further out the press still adds a vertex, which is what the mode says.
 */
const ZONE_GRAB_M = 1.0;



/**
 * Cubic ease-in-out function for smooth camera animations.
 * @param {number} t - progress 0→1
 * @returns {number} eased value 0→1
 */
function easeInOut(t) { return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2; }

// ── Sky texture ──
// Equirectangular sky texture loaded from public/sky.jpg

/**
 * Load the equirectangular sky texture from public/sky.jpg.
 * Used as both the scene background and environment map for reflections.
 * @returns {THREE.Texture} the equirectangular sky texture
 */
function createSkyTexture() {
    const loader = new THREE.TextureLoader();
    const texture = loader.load(process.env.PUBLIC_URL + '/sky.jpg');
    texture.mapping = THREE.EquirectangularRefractionMapping;
    return texture;
}
/**
 * True when the object — or any of its ancestors — is hidden.
 *
 * Raycaster has no notion of visibility: it happily hits `visible = false`
 * objects. A zone at opacity 0 is hidden by setting its group's `visible` flag,
 * so without this check an invisible zone would still steal clicks and could
 * be selected from the pitch. Zones remain selectable from the timeline /
 * toolbars, which is where you want to pick them up again.
 *
 * @param {THREE.Object3D} obj - the raycast hit object
 * @returns {boolean} true when the hit (or its container) is not drawn
 */
function isHiddenByAncestor(obj) {
    let c = obj;
    while (c) {
        if (c.visible === false) return true;
        c = c.parent;
    }
    return false;
}

/** Scratch vectors for the per-frame label pass (avoid per-frame allocation). */
const _labelCamSpace = new THREE.Vector3();
const _labelNdcA = new THREE.Vector3();
const _labelNdcB = new THREE.Vector3();
const _labelUp = new THREE.Vector3();
const _labelWorld = new THREE.Vector3();

/** Target on-screen text height (px) for player name labels. */
const PLAYER_LABEL_TEXT_PX = 15;

/**
 * Screen scale at a world position: how many screen pixels one world unit spans
 * vertically at that depth. Used to keep name labels a constant on-screen size
 * no matter the zoom, and works for both the perspective (3D) and orthographic
 * (2D) cameras.
 *
 * The unit offset is taken along the CAMERA's up axis, because that is what a
 * sprite's local +Y maps to on screen. Using world +Y instead would collapse in
 * the 2D top-down camera, where the screen-vertical axis is world Z.
 *
 * @param {THREE.Camera} camera - the active camera (matrices must be up to date)
 * @param {THREE.Vector3} worldPos - the point to measure at
 * @param {number} viewportHeightPx - renderer height in CSS pixels
 * @returns {number|null} pixels per world unit, or null when the point is
 *                        behind the camera (no meaningful screen scale)
 */
function pixelsPerWorldUnitAt(camera, worldPos, viewportHeightPx) {
    if (!camera || !worldPos || !(viewportHeightPx > 0)) return null;
    // Behind (or on) the camera plane — projection mirrors and the scale is junk
    _labelCamSpace.copy(worldPos).applyMatrix4(camera.matrixWorldInverse);
    if (_labelCamSpace.z >= 0) return null;
    _labelUp.setFromMatrixColumn(camera.matrixWorld, 1).normalize();
    _labelNdcA.copy(worldPos).project(camera);
    _labelNdcB.copy(worldPos).add(_labelUp).project(camera);
    // NDC spans -1…1 vertically, so half the viewport height per NDC unit.
    const pxPerUnit = Math.abs(_labelNdcB.y - _labelNdcA.y) * 0.5 * viewportHeightPx;
    return pxPerUnit > 0 ? pxPerUnit : null;
}

function pickBestRoot(intersections) { return firstItemHit(intersections).root; }


/**
 * Dispose geometries and materials of a removed Object3D.
 * Skips GLB-cloned objects (players, ball) that share resources with the cache —
 * disposing those would corrupt the cached base mesh.
 * @param {THREE.Object3D} obj - the object being removed from the scene
 */
function disposeObject3D(obj) {
    obj.traverse((child) => {
        if (child.isMesh || child.isLine || child.isLineLoop) {
            if (child.userData?.playerInfo || child.userData?.isBall) return; // cached GLB clone
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                if (Array.isArray(child.material)) child.material.forEach(m => m.dispose());
                else child.material.dispose();
            }
        }
    });
}

// ── Interaction silhouette tint ──
// The item being DRAGGED glows along its silhouette (sky blue), and while a
// ball is dragged over a player the potential RECEIVER glows green — the
// interaction state is unmistakable without touching the GUI. Materials are
// swapped for clones on tint and the originals restored on clear, so shared
// GLB materials (players, balls) never leak the glow to other items.
const DRAG_TINT = { hex: 0x38bdf8, intensity: 0.55 };
const RECEIVER_TINT = { hex: 0x22c55e, intensity: 0.7 };
// Persistent marker for members of the drag GROUP (Shift+click) — a dimmer
// sky so the whole formation reads as "moves together" at a glance.
const GROUP_TINT = { hex: 0x1d4ed8, intensity: 0.3 };
// The SELECTED item wears a soft white glow — "this is what my keys act on".
// The selection ring at the feet is easy to lose among overlapping players;
// the silhouette is not. Priority: drag > receiver > select > group marker.
const SELECT_TINT = { hex: 0xf8fafc, intensity: 0.22 };
// The MARK ghost wears the strip's rose (ATTACH_COLOR in TimelineEditor): the
// MARK lane, the compass and the ghost on the pitch are the same verb, so they
// are the same colour. A coach who learns that rose means "marking" in one
// place should find it in the other two.
const MARK_GHOST_COLOR = 0xfb7185;

function setSilhouetteTint(rootObj, tint) {
    if (!rootObj) return;
    rootObj.traverse((node) => {
        if (!node.isMesh || !node.material) return;
        if (node.userData?.isShadow || node.userData?.isPlayerLabel) return;
        if (node.userData?.__kvTint) return; // already tinted — idempotent
        const orig = node.material;
        const mats = Array.isArray(orig) ? orig : [orig];
        const tinted = mats.map((m) => {
            const c = m.clone();
            if (c.emissive) {
                c.emissive.setHex(tint.hex);
                c.emissiveIntensity = tint.intensity;
            } else if (c.color) {
                // Basic materials (zone fills/lines): push the colour toward
                // the tint and lift opacity a touch — same "it's live" signal.
                c.color.lerp(new THREE.Color(tint.hex), 0.45);
                if (c.transparent) c.opacity = Math.min(1, (c.opacity ?? 1) + 0.15);
            }
            return c;
        });
        node.userData.__kvTint = orig;
        node.material = tinted.length === 1 ? tinted[0] : tinted;
    });
}

function clearSilhouetteTint(rootObj) {
    if (!rootObj) return;
    rootObj.traverse((node) => {
        if (!node.isMesh || !node.userData?.__kvTint) return;
        const cur = Array.isArray(node.material) ? node.material : [node.material];
        for (const m of cur) if (m && m !== node.userData.__kvTint) m.dispose();
        node.material = node.userData.__kvTint;
        delete node.userData.__kvTint;
    });
}

/** Drag-tint key for a scene object ('ball:id', 'cone:id', 'zone:id', ...). */
function dragTintKeyOfObject(o) {
    if (!o) return null;
    const d = o.userData || {};
    if (d.isBall && d.ballId) return `ball:${d.ballId}`;
    if (d.isCone && d.coneId) return `cone:${d.coneId}`;
    if (d.isTarget && d.targetId) return `target:${d.targetId}`;
    if (d.isZone && d.zoneId) return `zone:${d.zoneId}`;
    if (d.playerInfo && d.side) return `player:${d.side}:${d.playerIndex}`;
    return null;
}

/** Lookup key for a group member descriptor ({side,index} or {type,id}). */
function groupKeyOf(g) {
    if (!g) return null;
    if (g.type === 'player' || g.side !== undefined) return `player:${g.side}:${g.index}`;
    return `${g.type}:${g.id}`;
}

// ── Find the 3D object matching a selectedItem descriptor ──
function findSceneObject(scene, item, homeTeam, awayTeam) {
    if (!item || !scene) return null;
    if (item.type === 'player') {
        const team = item.side === 'home' ? homeTeam : awayTeam;
        const n = String(item.index + 1);
        const info = team?.playerInfo?.[item.index] || { name: `P${n}`, number: n };
        const byName = scene.getObjectByName(getPlayerObjectName(info, item.side));
        if (byName) return byName;
        let found = null;
        scene.traverse((c) => {
            if (c?.userData?.playerInfo && c?.userData?.side === item.side && c?.userData?.playerIndex === item.index) found = c;
        });
        return found;
    }
    if (item.type === 'ball') {
        let found = null;
        scene.traverse((c) => { if (!found && c.userData?.isBall && c.userData?.ballId === item.id) found = c; });
        return found;
    }
    if (item.type === 'cone') {
        let found = null;
        scene.traverse((c) => { if (!found && c.userData?.coneId === item.id) found = c; });
        return found;
    }
    if (item.type === 'target') {
        let found = null;
        scene.traverse((c) => { if (!found && c.userData?.targetId === item.id) found = c; });
        return found;
    }
    if (item.type === 'zone') {
        let found = null;
        scene.traverse((c) => { if (!found && c.userData?.zoneId === item.id) found = c; });
        return found;
    }
    return null;
}

/**
 * The nav mode the device should start in.
 *
 * 'pan' maps a single finger to PAN, and no touch gesture then orbits — so on
 * a phone the camera could not be rotated at all until the coach found the
 * orbit button and tapped it. 'orbit' is the mode a finger can use on its own:
 * one finger rotates, two fingers pan and pinch to zoom. A mouse keeps the old
 * default, where left-drag slides the pitch and right-drag orbits.
 */
export function defaultNavMode() {
    // One probe, shared with everything else that grows for a thumb (see
    // coarsePointer in platform.js) — the app must not hold two opinions about
    // what kind of device it is on.
    return coarsePointer() ? 'orbit' : 'pan';
}

// ── Apply orbit/pan config to OrbitControls (shared helper) ──
/**
 * Configure OrbitControls for the current interaction mode.
 * @param {THREE.OrbitControls} controls - the controls instance to configure
 * @param {string} mode - 'orbit' or 'pan' (left-drag behaviour)
 * @param {boolean} isOrtho - true when in 2D orthographic (top-down) mode
 */
export function applyControlsConfig(controls, mode, isOrtho, following = false) {
    if (!controls) return;
    controls.enableDamping = false; // Stop immediately when a camera gesture ends.
    controls.maxPolarAngle = (Math.PI / 2) - 0.1;
    controls.enableZoom = true; // scroll/pinch always zooms
    controls.zoomToCursor = !following; // Pinch midpoint/wheel cursor anchors the zoom.
    controls.screenSpacePanning = false; // Keep pan and focus on the pitch plane.
    controls.minDistance = 1;
    controls.maxDistance = 250;

    if (isOrtho) {
        // 2D top-down: left-drag = pan, no rotate
        controls.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.PAN };
        controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_PAN };
        controls.enableRotate = false;
        controls.enablePan = true;
        controls.screenSpacePanning = false; // pan on XZ plane for top-down
    } else if (following) {
        controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE };
        controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_ROTATE };
        controls.enableRotate = true;
        controls.enablePan = false; // keep the orbit centre anchored to the followed item
    } else if (mode === 'orbit') {
        // 3D orbit: left-drag = rotate, right-drag = rotate (same), scroll = zoom
        controls.mouseButtons = { LEFT: THREE.MOUSE.ROTATE, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE };
        controls.touches = { ONE: THREE.TOUCH.ROTATE, TWO: THREE.TOUCH.DOLLY_PAN };
        controls.enableRotate = true;
        controls.enablePan = true;
    } else {
        // 3D pan: left-drag = pan, right-drag = always orbit, scroll = zoom
        controls.mouseButtons = { LEFT: THREE.MOUSE.PAN, MIDDLE: THREE.MOUSE.DOLLY, RIGHT: THREE.MOUSE.ROTATE };
        controls.touches = { ONE: THREE.TOUCH.PAN, TWO: THREE.TOUCH.DOLLY_PAN };
        controls.enableRotate = true;  // keep enabled so right-click can orbit
        controls.enablePan = true;
    }
    controls.update();
}

// ═══════════════════════════════════════════════════════════════
// VIEWPORT3D COMPONENT
// ═══════════════════════════════════════════════════════════════

/**
 * KickViz main 3D viewport component.
 *
 * Receives pitch state, team data, and interaction callbacks from the parent
 * (App.js).  Manages the full Three.js lifecycle: renderer, dual cameras,
 * OrbitControls, post-processing, raycasting, animation, and the UI overlay.
 *
 * @param {object} props - See the destructured params below for all props.
 * @returns {JSX.Element} The viewport container with canvas + UI controls.
 */
function Viewport3D({width, height, pitchState, homeTeamState, awayTeamState,
    actionMode, onActionModeChange, pendingObjectType,
    captureOpen, onCaptureOpenChange,
    screenshotTick = 0, // P in App keyboard: bump to take a screenshot from anywhere
    recordToggleTick = 0, // ⇧P in App keyboard: bump to start/stop the video recording
    onCaptureNotify = null, // (message) => void — capture events worth a toast (recording started)
    selectedItem, onSelectItem,
    groupItems = [], // Shift+click group: dragging one member moves them all
    cameraRequest = null, // one-shot { kind: 'reset'|'top'|'low', n } from App keyboard (1/2/3)
    onPlayerPlace, onObjectPlace, onObjectMove, onPlayerRemove, onObjectRemove,
    onUndo, canUndo, onRedo, canRedo,
    undoCount = 0, undoAtLimit = false, redoCount = 0,
    onShareError = null, // a failed image share is announced in-app, never by window.alert
    undoLabel = null, redoLabel = null, // top-of-stack names for the button tooltips
    onNextBranchPlay = null, nextBranchPlaying = false,
    onMasterPlay = null, masterPlaying = false, playbackSpeed = 1, onPlaybackSpeedChange = null,
    onUndoStart = null, onUndoEnd = null, // drag gesture hooks — snapshot pre-drag state once
    onItemHeightChange, selectedItemHeight,
    onZonePointsChange, onZoneColorChange, onZoneOpacityChange, onZoneModeChange, onZoneRadiusChange, onZoneClosedChange, onZonePointRefChange, onZoneThicknessChange, onZonePointCountChange,
    onZonePointSelected = null, // (index|null) — the vertex the pitch editor has grabbed
zoneEditRequest = null, zoneLinkRequest = null, onZoneEditExit = null, // one-shot requests from the zone-visuals editor + edit-mode exit callback
onBallDrop = null, attachMode = null, onAttachPlayerSelect = null, // ball attach-pick mode: clicking a player attaches
onActionPrompt = null, onZoneShapePrompt = null, // mouse double-click on an item → its prompt (3D twin of the timeline dot tap)
 onPlayerBallDrop = null, // player dragged onto a ball → offer to give it the ball
 onMarkPlayerDrop = null, // player dragged onto ANOTHER player → offer to MARK them
 markPreview = null,      // the MARK compass is open: { value, dir, dist } — show where the marker WILL stand
 onMarkSpotPick = null,   // the compass is open: a tap on open turf sets bearing + gap there ({ gx, gz })
 onObjectDrop = null,      // cone/target dragged onto a player/ball (or off its carrier) → attach/detach
 onZoneVertexCommit = null, // the zone editor's Add/Remove tools → say what changed
 onZoneDrop = null,       // zone dragged onto a player/ball (or off its carrier) → attach/detach
 onPitchLongPress = null, // touch: hold an empty spot in select mode → the VIEW menu (camera)
 onSegmentBend = null,    // live bend-drag on a curved segment's apex handle
 onSegmentArch = null,    // live arch-drag on an arched ball segment's flight handle
    onAssetsLoaded,
    keyframes = [],       // V2 keyframe tracks (movement = position track)
    playing = false,
    timelineTime = 0,     // playhead time (seconds) driving interpolation
    duration = 60,        // master timeline length (seconds) — graphic path windows extend to this
}) {
    const { t } = useI18n();

    const PITCH_PLACEMENT_OFFSET_Y_PLAYER = 0;
    const defaultCameraPosition = new THREE.Vector3(0.0, 55.0, 55.0);
    const [pitchProperties] = useState({ pitchLength: 105, pitchWidth: 68, goalWidth: 7.32, goalHeight: 2.44, lineThickness: 0.12, boxWidth: 40.32, boxLength: 16.5, smallBoxWidth: 18.32, smallBoxLength: 5.5, centerCircleRadius: 9.15, penaltyArcRadius: 9.15, penaltySpotOffset: 11 });
    const [isOrtho, setIsOrtho] = useState(false);
    const [followPicking, setFollowPicking] = useState(false);
    const [followTarget, setFollowTarget] = useState(null);
    const refFollowPicking = useRef(false);
    const refFollowTarget = useRef(null);
    const followScratch = useRef(null);
    if (!followScratch.current) followScratch.current = createFollowScratch();
    const stopFollow = () => {
        refFollowPicking.current = false; refFollowTarget.current = null;
        setFollowPicking(false); setFollowTarget(null);
    };
    const [navMode, setNavMode] = useState(defaultNavMode);     // 'orbit' | 'pan'
    // Player name labels over the meshes — toggled from the viewport nav.
    const [namesVisible, setNamesVisible] = useState(true);
    const refNamesVisible = useRef(true); // read per frame by the label pass
    const [zoneEditOn, setZoneEditOn] = useState(false);
    const [zoneAction, setZoneAction] = useState('add'); // 'add' | 'remove'
    const [loadingProgress, setLoadingProgress] = useState(0); // 0-100 asset loading progress
    const [bloomOn, setBloomOn] = useState(true);
    const [assetsLoaded, setAssetsLoaded] = useState(false); // true when all 3D assets are ready
    // Bumped once character/kit assets for the current teams finish preloading.
    // The player rebuild effect depends on it so players are (re)built with the
    // real meshes even when their data arrives mid-preload (e.g. a scenario
    // auto-loaded at boot, before base_mesh.glb is cached).
    const [charAssetsVersion, setCharAssetsVersion] = useState(0);
    const [actionPickerFor, setActionPickerFor] = useState(null); // { side, playerIndex, actionIndex } when action icon picker is open
    const [zoneLinkTarget, setZoneLinkTarget] = useState(null); // { zoneId, pointIndex } when linking a zone point to a player
    const [zoneSelectPoint, setZoneSelectPoint] = useState(null); // point index currently selected for dragging
    const refZoneLinkTarget = useRef(null);
    const refAttachMode = useRef(null); // ball attach-pick mode: trackId while picking a player
    const refZoneSelectPoint = useRef(null);
    useEffect(() => { refZoneLinkTarget.current = zoneLinkTarget; }, [zoneLinkTarget]);
    useEffect(() => { refAttachMode.current = attachMode; }, [attachMode]);
    useEffect(() => { refZoneSelectPoint.current = zoneSelectPoint; }, [zoneSelectPoint]);
    // Tell App which vertex is selected (null = none) so Ctrl+arrows can nudge
    // THAT vertex instead of the radius/height they dial otherwise.
    useEffect(() => { refOnZonePointSelected.current?.(zoneSelectPoint); }, [zoneSelectPoint]);
    // One-shot requests from the zone-visuals editor: enter polygon edit mode,
    // or start linking a dynamic vertex to a player/ball on the pitch.
    useEffect(() => { if (zoneEditRequest) { setZoneEditOn(true); setZoneAction('add'); if (zoneEditRequest.selectPoint != null) setZoneSelectPoint(zoneEditRequest.selectPoint); } }, [zoneEditRequest]);
    useEffect(() => { if (zoneLinkRequest) { setZoneLinkTarget({ zoneId: zoneLinkRequest.zoneId, pointIndex: zoneLinkRequest.pointIndex }); } }, [zoneLinkRequest]);
    // Esc unwinds the pitch editors ONE LAYER at a time, like everywhere else:
    // drop the grabbed vertex, then LEAVE EDIT ZONE mode (the red ✕'s keyboard
    // twin — the tutorial's last step was mouse-only, and Esc used to close the
    // visuals editor over in App while this panel stayed on the pitch, orphaned).
    useEffect(() => {
        const onEsc = (e) => {
            if (e.key === 'Escape') {
                refFollowPicking.current = false; setFollowPicking(false); setZoneLinkTarget(null);
                if (zoneSelectPoint !== null) { setZoneSelectPoint(null); return; }
                if (zoneEditOn) {
                    setZoneEditOn(false); setZoneSelectPoint(null);
                    if (onZoneEditExit) onZoneEditExit();
                }
            }
        };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, [zoneSelectPoint, zoneEditOn, onZoneEditExit]);
    // Changing the selection exits zone edit mode + vertex-linking (the visuals
    // editor auto-dismisses in App.js, and its edit binding is cleared).
    useEffect(() => { setZoneEditOn(false); setZoneLinkTarget(null); setZoneSelectPoint(null); }, [selectedItem]);
    const raycaster = useRef(new THREE.Raycaster()).current;

    const refPointerStart = useRef({ x: 0, y: 0, active: false, dragged: false, pointerId: -1 });
    // Silhouette glow state: the dragged item (key + live object, which scene
    // rebuilds may replace mid-drag) and the ball-drop receiver candidate.
    const refDragTint = useRef(null);      // { key, obj }
    const refReceiverTint = useRef(null);  // THREE.Object3D | null

    const refExportElement = useRef(null);
    const refRenderer = useRef(null);
    const refPerspectiveCamera = useRef(null);
    const refOrthoCamera = useRef(null);
    const refCamera = useRef(null);
    const refScene = useRef(null);
    const refControls = useRef(null);
    const refIsOrtho = useRef(false);
    const refNavMode = useRef(defaultNavMode()); // seeded, so the first frame matches the button
    const refRenderPass = useRef(null);
    const refCameraAnim = useRef(null);
    const refPointerCleanup = useRef(null);
    const refTimer = useRef(new THREE.Timer());
    const refDirLight = useRef(null);
    const refComposer = useRef(null);
    // ── SCREENSHOT: the capture panel's verb, also callable from App's keyboard
    // (P) via the screenshotTick prop — one key replaces camera-button → panel
    // → Screenshot. The share sheet / download announces itself; no toast. ──
    const doScreenshot = async () => {
      if (!refRenderer.current) return;
      refRenderer.current.render(refScene.current, refCamera.current);
      const canvas = refRenderer.current.domElement;
      const filename = `kickviz_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')}.png`;
      const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      if (!blob) return;
      if (isMobileBuild) {
        try { await getRuntimeAdapter().shareImage(blob, filename); }
        catch (error) {
          // The failure is announced IN the workspace, not by a system strip:
          // on a phone an alert() is the ugliest, least tappable thing on screen.
          if (refOnShareError.current) refOnShareError.current(`Could not share image: ${error.message}`);
          else console.error('shareImage failed:', error);
        }
        return;
      }
      // Share to Photos via the Web Share API when available…
      if (typeof navigator.share === 'function') {
        try {
          const file = new File([blob], filename, { type: 'image/png' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: 'KickViz Screenshot' });
            return;
          }
        } catch (err) {
          if (err.name === 'AbortError') return;
        }
      }
      // …desktop fallback: download the PNG
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    useEffect(() => {
      if (screenshotTick > 0) doScreenshot();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [screenshotTick]);
    const refBloomPass = useRef(null);
    const refSelectionRing = useRef(null);
    const refDynamicZones = useRef([]);
    const refSelectionRingCenter = useRef(new THREE.Vector3());

    // Refs for callbacks and props accessed in event handlers
    const refActionMode = useRef(actionMode);
    const refPendingObjectType = useRef(pendingObjectType);
    const refSelectedItem = useRef(selectedItem);
    const refOnSelectItem = useRef(onSelectItem);
    const refGroupItems = useRef(groupItems);
    useEffect(() => { refGroupItems.current = groupItems; }, [groupItems]);
    const refOnShareError = useRef(onShareError);
    useEffect(() => { refOnShareError.current = onShareError; }, [onShareError]);
    // Player objects currently carrying the persistent GROUP_TINT marker.
    const refGroupTinted = useRef(new Map()); // key → { obj, tint } (group/selection markers)
    const refGroupLongPress = useRef(null); // touch long-press timer (group toggle)
    const refBendHandle = useRef(null);  // orange apex handle on a curved segment
    const refArchHandle = useRef(null);  // violet mid-flight handle on an arched ball segment
    const refBendDrag = useRef(null);    // live bend/arch drag { kind, trackId, from, to, p0, p1 }
    const refOnSegmentBend = useRef(onSegmentBend);
    const refOnSegmentArch = useRef(onSegmentArch);
    useEffect(() => { refOnSegmentBend.current = onSegmentBend; }, [onSegmentBend]);
    useEffect(() => { refOnSegmentArch.current = onSegmentArch; }, [onSegmentArch]);
    const refOnPlayerPlace = useRef(onPlayerPlace);
    const refOnObjectPlace = useRef(onObjectPlace);
    const refOnPitchLongPress = useRef(onPitchLongPress);
    useEffect(() => { refOnPitchLongPress.current = onPitchLongPress; }, [onPitchLongPress]);
    const refOnBallDrop = useRef(onBallDrop);
    useEffect(() => { refOnBallDrop.current = onBallDrop; }, [onBallDrop]);
    const refOnPlayerBallDrop = useRef(onPlayerBallDrop);
    useEffect(() => { refOnPlayerBallDrop.current = onPlayerBallDrop; }, [onPlayerBallDrop]);
    const refOnMarkPlayerDrop = useRef(onMarkPlayerDrop);
    useEffect(() => { refOnMarkPlayerDrop.current = onMarkPlayerDrop; }, [onMarkPlayerDrop]);
    // The keyboard twins already announce every vertex edit; the pitch tools did
    // not, so a phone user adding a vertex got a shape that changed and no word
    // about what happened or how to undo it. One channel, the same words.
    const refOnZoneVertexCommit = useRef(onZoneVertexCommit);
    useEffect(() => { refOnZoneVertexCommit.current = onZoneVertexCommit; }, [onZoneVertexCommit]);
    const refOnZoneDrop = useRef(onZoneDrop);
    useEffect(() => { refOnZoneDrop.current = onZoneDrop; }, [onZoneDrop]);
    const refOnObjectDrop = useRef(onObjectDrop);
    useEffect(() => { refOnObjectDrop.current = onObjectDrop; }, [onObjectDrop]);
    const refOnObjectMove = useRef(onObjectMove);
    const refOnPlayerRemove = useRef(onPlayerRemove);
    const refOnObjectRemove = useRef(onObjectRemove);
    const refOnUndoStart = useRef(onUndoStart);
    const refOnUndoEnd = useRef(onUndoEnd);
    const refHomeTeamState = useRef(homeTeamState);
    const refAwayTeamState = useRef(awayTeamState);

    // Track which 3D object currently has selection applied (for cleanup)
    const refSelectedObj3D = useRef(null);

    const refItemDrag = useRef(null); // { root, dragging } for direct item drag

    // Zone edit refs
    const refZoneEditOn = useRef(false);
    const refZoneAction = useRef('add');
    const refOnZonePointsChange = useRef(onZonePointsChange);
    const refOnZonePointSelected = useRef(onZonePointSelected);
    const refOnZonePointRefChange = useRef(onZonePointRefChange);
    const refOnAttachPlayerSelect = useRef(onAttachPlayerSelect);
    const refOnActionPrompt = useRef(onActionPrompt);
    const refOnMarkSpotPick = useRef(onMarkSpotPick);
    const refMarkPreview = useRef(markPreview);
    const refOnZoneShapePrompt = useRef(onZoneShapePrompt);
    const refZonePointDrag = useRef(null); // { zoneId, pointIndex } for zone point drag
    // Last item tap { t, key } — a SECOND touch tap on the same item within
    // 400 ms flies the camera to it (touch twin of click + F).
    const refLastTap = useRef({ t: 0, key: '' });
    const refSelectedZonePoints = useRef(null);
    // Parallel refs for the selected zone's per-vertex attachments — kept in
    // sync with refSelectedZonePoints so add/remove NEVER offsets/loses the
    // existing points' attachment info (they are the applied visual shape).
    const refSelectedZoneRefs = useRef(null);

    /**
     * Push an edit result into the zone object's appliedVisualValue IMMEDIATELY.
     * The sync effect (which depends on keyframes) re-reads this value after
     * every edit — if we only write the keyframes (async React state), the
     * effect can read the PREVIOUS frame's value and clobber our refs,
     * making fast edits (click-click, drag end) appear to revert.
     */
    const applyZoneVisualToObject = (zoneId, points, refs) => {
        const zo = refObjLookup.current.get(`zone:${zoneId}`);
        if (!zo) return;
        const prev = zo.userData.appliedVisualValue || {};
        // The edit buffer is DISPLAY space; appliedVisualValue must stay in
        // ANCHOR space (the key's captured centre) because rebuildZoneObject
        // draws it relative to that anchor. Convert: anchor = display − D.
        const { dx, dz } = computeZoneDisplayOffset(zoneId);
        const outPts = (dx === 0 && dz === 0)
            ? points.map(p => [...p])
            : points.map(p => [p[0] - dx, p[1] ?? 0, p[2] - dz]);
        zo.userData.appliedVisualValue = {
            ...prev,
            points: outPts,
            pointRefs: (refs || []).map(r => ({ ref: r?.ref ?? null })),
        };
    };

    // Animation refs (accessed in animate loop)
    const refObjLookup = useRef(new Map()); // O(1) lookup: 'player:home:3' / 'ball:ball-1' → THREE.Object3D

    // ── Persistent interaction markers (group membership + selection) ──
    // Group members wear a dim sky tint ("these move together"); the SELECTED
    // item wears a soft white tint ("my keys act on this"). Selection wins over
    // the group marker on the same object; transient glows (drag / receiver)
    // always win over both and are restored on release.
    const applyPersistentTints = useCallback(() => {
        const wanted = new Map(); // key → tint
        for (const g of (refGroupItems.current || [])) {
            const k = groupKeyOf(g);
            if (k) wanted.set(k, GROUP_TINT);
        }
        const sel = refSelectedItem.current;
        const selKey = sel && ['player', 'ball', 'cone', 'target', 'zone'].includes(sel.type)
            ? groupKeyOf(sel) : null;
        if (selKey) wanted.set(selKey, SELECT_TINT);
        // Objects owned by a live glow are skipped — the release path re-applies.
        const live = new Set();
        if (refDragTint.current?.key) live.add(refDragTint.current.key);
        if (refItemDrag.current?.dragging) for (const m of (refItemDrag.current.group || [])) live.add(groupKeyOf(m));
        for (const [key, entry] of refGroupTinted.current) {
            if (!wanted.has(key)) { clearSilhouetteTint(entry.obj); refGroupTinted.current.delete(key); }
        }
        for (const [key, tint] of wanted) {
            if (live.has(key)) continue;
            const obj = refObjLookup.current.get(key);
            if (!obj) continue;
            if (refReceiverTint.current === obj) continue;
            const prev = refGroupTinted.current.get(key);
            if (prev && prev.obj === obj && prev.tint === tint) continue; // already correct
            if (prev) { clearSilhouetteTint(prev.obj); refGroupTinted.current.delete(key); }
            // A transient glow may have stolen and released this object,
            // leaving the map stale — clear defensively before re-tinting.
            clearSilhouetteTint(obj);
            setSilhouetteTint(obj, tint);
            refGroupTinted.current.set(key, { obj, tint });
        }
    }, []);
    useEffect(() => { applyPersistentTints(); }, [groupItems, applyPersistentTints]);
    // Player name labels currently in the scene — sized each frame so they keep
    // a constant on-screen size and honour the "Names" toggle (see animate()).
    const refPlayerLabels = useRef([]);
    const refBallRollState = useRef(new Map()); // ballId → { prevPos: THREE.Vector3, rollAxis: THREE.Vector3 }
    const refPitchState = useRef(null);
    // V2 keyframe interpolation refs (read every frame without re-renders)
    const refKeyframes = useRef([]);
    const refTimelineTime = useRef(0);
    const refPlaying = useRef(playing);
    refPlaying.current = playing;
    const refGraphicPaths = useRef(new Map()); // playerId → { group, win, sig }


    const refKeyframesSig = useRef('');
    // ── THE MARK GHOST ──
    // The compass chooses a BEARING and a GAP around the marked player. Both are
    // numbers on a dial; neither is a place on the pitch. A coach tapping "back
    // left, 2 m" had no way to see where that put the marker until the key was
    // already written. This draws the spot live, in the same rose the MARK lane
    // in the strip is drawn with, so the dial, the lane and the pitch all speak
    // about one thing. It is a preview, not an object: it writes nothing, and it
    // disappears the moment the compass closes.
    /**
     * One marker's worth of preview: a silhouette, a ring under it, and the gap
     * drawn as a dashed line from the marked player out to it. Nothing in here
     * is pickable — a preview you can accidentally grab is a bug, because the
     * ghost would swallow the tap meant for the player underneath it.
     */
    function makeGhostSlot() {
        const slot = new THREE.Group();
        // A silhouette, not a solid body: the coach has to see the marked
        // player THROUGH the preview.
        const body = new THREE.Mesh(
            new THREE.CylinderGeometry(0.34, 0.34, 1.45, 14),
            new THREE.MeshBasicMaterial({ color: MARK_GHOST_COLOR, transparent: true, opacity: 0.42, depthWrite: false }));
        body.name = 'markGhostBody';
        const head = new THREE.Mesh(
            new THREE.SphereGeometry(0.27, 16, 12),
            new THREE.MeshBasicMaterial({ color: MARK_GHOST_COLOR, transparent: true, opacity: 0.5, depthWrite: false }));
        head.name = 'markGhostHead';
        const ring = new THREE.Mesh(
            new THREE.RingGeometry(0.46, 0.62, 48),
            new THREE.MeshBasicMaterial({ color: MARK_GHOST_COLOR, transparent: true, opacity: 0.75, side: THREE.DoubleSide, depthWrite: false }));
        ring.rotation.x = -Math.PI / 2;
        ring.name = 'markGhostRing';
        const line = new THREE.Line(
            new THREE.BufferGeometry(),
            new THREE.LineDashedMaterial({ color: MARK_GHOST_COLOR, dashSize: 0.28, gapSize: 0.18, transparent: true, opacity: 0.85 }));
        line.name = 'markGhostGap';
        [body, head, ring, line].forEach(m => { m.raycast = () => {}; });
        slot.add(body, head, ring, line);
        return slot;
    }
    /** Park one slot at the spot its leader's marker would actually take. */
    function placeGhostSlot(slot, s) {
        const byName = (n) => slot.children.find(c => c.name === n);
        byName('markGhostBody').position.set(s.gx, 0.78, s.gz);
        byName('markGhostHead').position.set(s.gx, 1.72, s.gz);
        byName('markGhostRing').position.set(s.gx, 0.02, s.gz);
        // The gap, drawn: the line is what makes "1.5 m" a distance rather than
        // a number on a slider.
        const line = byName('markGhostGap');
        line.geometry.setFromPoints([new THREE.Vector3(s.mp[0], 0.03, s.mp[2]), new THREE.Vector3(s.gx, 0.03, s.gz)]);
        line.computeLineDistances();
    }
    const refMarkGhost = useRef(null);
    useEffect(() => {
        const scene = refScene.current;
        const drop = () => {
            if (refMarkGhost.current && scene) { scene.remove(refMarkGhost.current); disposeObject3D(refMarkGhost.current); }
            refMarkGhost.current = null;
        };
        if (!scene || !markPreview || !markPreview.value || markPreview.value === 'none') { drop(); return; }
        // ONE GHOST PER LEADER. A group dial sets the same bearing and gap for
        // every marker in the group, and each of them marks a different player —
        // a single ghost would preview the spot only ONE of them takes, and a
        // preview that shows less than the release will do is a gesture that
        // lies to the finger.
        const refs = (Array.isArray(markPreview.leaders) && markPreview.leaders.length)
            ? markPreview.leaders : [markPreview.value];
        const spots = [];
        for (const ref of refs) {
            const [mSide, mIdxRaw] = String(ref).split(':');
            const mIdx = parseInt(mIdxRaw, 10);
            if ((mSide !== 'home' && mSide !== 'away') || !Number.isFinite(mIdx)) continue;
            // The leader's position AT THE PLAYHEAD, not its authored spot.
            // pitchState carries the base position, and for a player who has keys that
            // is where they END UP — so the ghost sat at the far end of the route while
            // the player was still standing somewhere else, and the preview told you
            // where the mark would land once the drill finished rather than where it
            // would land from where you are looking now. sampleEffectivePosition is the
            // same maths the renderer uses to place the real figure every frame, so the
            // ghost and the player cannot disagree. Falls back to the authored spot when
            // the leader has no track at all.
            const leaderTrack = (refKeyframes.current || []).find(k => k.id === `${mSide}:${mIdx}`);
            const mp = (leaderTrack
                    ? sampleEffectivePosition(leaderTrack, refTimelineTime.current ?? 0, refKeyframes.current || [])
                    : null)
                || pitchState?.[mSide]?.players?.[mIdx]?.position;
            if (!mp) continue;
            // The SAME maths the sampler uses to place a real marker, so the
            // ghost cannot disagree with where the player actually lands.
            const off = markOffset(markPreview.dir || 0, markPreview.dist ?? MARK_DEFAULT_DIST, mSide);
            spots.push({ mp, gx: mp[0] + off[0], gz: mp[2] + off[2] });
        }
        if (!spots.length) { drop(); return; }

        let g = refMarkGhost.current;
        if (!g) {
            // The group stays at the ORIGIN and every piece carries its own world
            // position, so the gap line is a CHILD of the ghost: one remove() and
            // one dispose() takes the whole preview away, with nothing left
            // floating on the pitch after the compass closes.
            g = new THREE.Group();
            g.userData.isMarkGhost = true;
            // Not pickable, twice over, and both ways matter. `pickable: false`
            // is this app's own convention — sceneItemRoot walks the ancestors and
            // drops the branch — and the empty raycast means the ghost never even
            // reaches the hit list. A preview you can accidentally grab is a bug:
            // it would swallow the tap meant for the player underneath it.
            g.userData.pickable = false;
            refMarkGhost.current = g;
            scene.add(g);
        }
        // Slots follow the leader list: one added when the group grows, one
        // disposed when it shrinks, and nothing left behind when the dial closes.
        while (g.children.length > spots.length) {
            const dead = g.children[g.children.length - 1];
            g.remove(dead);
            disposeObject3D(dead);
        }
        while (g.children.length < spots.length) g.add(makeGhostSlot());
        spots.forEach((s, i) => placeGhostSlot(g.children[i], s));
    }, [markPreview?.value, markPreview?.leaders, markPreview?.dir, markPreview?.dist, pitchState, timelineTime]);
    // The pointer handlers read both through refs: the compass opens and closes
    // without re-binding the canvas listeners.
    useEffect(() => { refMarkPreview.current = markPreview; }, [markPreview]);
    useEffect(() => { refOnMarkSpotPick.current = onMarkSpotPick; }, [onMarkSpotPick]);
    useEffect(() => {
        refKeyframes.current = keyframes || [];
        refKeyframesSig.current = JSON.stringify(keyframes || []);
    }, [keyframes]);
    useEffect(() => { refTimelineTime.current = timelineTime || 0; }, [timelineTime]);

    /**
     * Show/hide the player name labels.
     * Mirrored into a ref because the per-frame label pass reads it directly
     * (no re-render needed to apply the change).
     */
    const handleToggleNames = useCallback(() => {
        const next = !refNamesVisible.current;
        refNamesVisible.current = next;
        setNamesVisible(next);
    }, []);

    // ── Camera toggle ──
    const toggleCamera = useCallback(() => {
        if (refCameraAnim.current || !refRenderer.current || !refPerspectiveCamera.current || !refOrthoCamera.current) return;
        if (refControls.current) refControls.current.enabled = false;
        if (!refIsOrtho.current) {
            // Switching to 2D: center on scene origin
            if (refControls.current) refControls.current.target.set(0, 0, 0);
            refCameraAnim.current = { t: 0, duration: 0.6, fromPos: refCamera.current.position.clone(), toPos: new THREE.Vector3(0, 80, 0.01), swapAtEnd: true };
        } else {
            const oldTarget = refControls.current ? refControls.current.target.clone() : new THREE.Vector3(0, 0, 0);
            refPerspectiveCamera.current.position.copy(refCamera.current.position);
            refCamera.current = refPerspectiveCamera.current;
            refIsOrtho.current = false;
            if (refRenderPass.current) refRenderPass.current.camera = refCamera.current;
            if (refControls.current) refControls.current.dispose();
            refControls.current = new OrbitControls(refCamera.current, refRenderer.current.domElement);
            refControls.current.target.copy(oldTarget);
            applyControlsConfig(refControls.current, refNavMode.current, false, !!refFollowTarget.current);
            refCameraAnim.current = { t: 0, duration: 0.6, fromPos: refCamera.current.position.clone(), toPos: defaultCameraPosition.clone(), swapAtEnd: false };
        }
    }, []);

    // ── Reset camera to default position + target (0,0,0) ──
    const resetCamera = useCallback(() => {
        if (refCameraAnim.current || !refCamera.current) return;
        if (refControls.current) refControls.current.enabled = false;
        // If in ortho mode, switch back to perspective first
        if (refIsOrtho.current) {
            const oldTarget = refControls.current ? refControls.current.target.clone() : new THREE.Vector3(0, 0, 0);
            refPerspectiveCamera.current.position.copy(refCamera.current.position);
            refCamera.current = refPerspectiveCamera.current;
            refIsOrtho.current = false;
            if (refRenderPass.current) refRenderPass.current.camera = refCamera.current;
            if (refControls.current) refControls.current.dispose();
            refControls.current = new OrbitControls(refCamera.current, refRenderer.current.domElement);
            refControls.current.target.copy(oldTarget);
            applyControlsConfig(refControls.current, refNavMode.current, false, !!refFollowTarget.current);
            setIsOrtho(false);
        }
        refCameraAnim.current = {
            t: 0, duration: 0.8,
            fromPos: refCamera.current.position.clone(),
            toPos: defaultCameraPosition.clone(),
            swapAtEnd: false,
        };
    }, []);

    // ── Zoom to selected object ──
    const zoomToSelected = useCallback(() => {
        if (refCameraAnim.current || !refCamera.current || !refScene.current) return;
        const item = refSelectedItem.current;
        if (!item) return;
        const obj = findSceneObject(refScene.current, item, refHomeTeamState.current, refAwayTeamState.current);
        if (!obj) return;

        const targetPos = new THREE.Vector3();
        obj.getWorldPosition(targetPos);

        // If in ortho mode, switch back to perspective first
        if (refIsOrtho.current) {
            refPerspectiveCamera.current.position.copy(refCamera.current.position);
            refCamera.current = refPerspectiveCamera.current;
            refIsOrtho.current = false;
            if (refRenderPass.current) refRenderPass.current.camera = refCamera.current;
            if (refControls.current) refControls.current.dispose();
            refControls.current = new OrbitControls(refCamera.current, refRenderer.current.domElement);
            refControls.current.target.copy(targetPos);
            applyControlsConfig(refControls.current, refNavMode.current, false, !!refFollowTarget.current);
            setIsOrtho(false);
        }

        if (refControls.current) refControls.current.enabled = false;

        // Camera position: offset from target at a close zoom distance
        const zoomDistance = 12;
        const direction = new THREE.Vector3(0.5, 0.7, 0.5).normalize(); // angled view
        const toPos = targetPos.clone().add(direction.multiplyScalar(zoomDistance));

        refCameraAnim.current = {
            t: 0, duration: 0.6,
            fromPos: refCamera.current.position.clone(),
            toPos,
            target: targetPos,
            swapAtEnd: false,
        };
    }, []);

    // ── One-shot camera presets from App keyboard (1 = reset, 2 = top, 3 = low, F = focus) ──
    useEffect(() => {
        if (!cameraRequest || !refRenderer.current || !refCamera.current) return;
        const kind = cameraRequest.kind;
        if (kind === 'focus') { zoomToSelected(); return; }
        if (kind === 'reset' || refIsOrtho.current) { resetCamera(); return; } // presets need perspective; one press to get back
        if (refCameraAnim.current) return;
        const to = kind === 'top' ? new THREE.Vector3(0, 80, 0.01)
            : kind === 'low' ? new THREE.Vector3(0, 9, 46)
            : defaultCameraPosition.clone();
        if (refControls.current) refControls.current.enabled = false;
        refCameraAnim.current = { t: 0, duration: 0.6, fromPos: refCamera.current.position.clone(), toPos: to, swapAtEnd: false };
    }, [cameraRequest, resetCamera, zoomToSelected]);

    // ── Apply/remove selection on 3D objects based on selectedItem prop ──
    // ═══════════════════════════════════════════════════════════
    // SELECTION VISUAL SYNC
    // ═══════════════════════════════════════════════════════════

    /**
     * Update the glowing ground ring to follow the currently selected item.
     * Called whenever selectedItem changes.
     */
    function syncSelectionVisual() {
        const scene = refScene.current;
        if (!scene) return;
        const item = refSelectedItem.current;

        if (refSelectedObj3D.current) {
            refSelectedObj3D.current = null;
        }

        // Remove old selection ring
        if (refSelectionRing.current) {
            scene.remove(refSelectionRing.current);
            refSelectionRing.current.geometry.dispose();
            refSelectionRing.current.material.dispose();
            refSelectionRing.current = null;
        }

        // Zone centre-cross is only visible while its zone is selected.
        // Runs on EVERY sync (selected or not) so the cross also hides when
        // the selection is cleared. The zone's desired state is ALSO stored
        // on the object (zoneCrossVisible) because the animate-loop rebuild
        // (rebuildZoneObject) can run after this sync and must re-apply it.
        let selectedZoneObj = null;
        if (item?.type === 'zone') {
            selectedZoneObj = findSceneObject(scene, item, refHomeTeamState.current, refAwayTeamState.current);
        }
        scene.traverse((c) => {
            if (!c.userData?.isZoneCenterCross) return;
            const zoneObj = c.parent;
            const isSel = zoneObj && zoneObj === selectedZoneObj;
            c.visible = isSel;
            zoneObj.userData.zoneCrossVisible = isSel;
        });

        if (item) {
            const obj = findSceneObject(scene, item, refHomeTeamState.current, refAwayTeamState.current);
            if (obj) {
                refSelectedObj3D.current = obj;

                // Create glowing ground selection ring
                let ringInner = 0.8, ringOuter = 1.1;
                let ringX = obj.position.x, ringZ = obj.position.z;

                if (item.type === 'zone') {
                    // Ring is centred on the zone's CENTRE CROSS (the group
                    // position = the fixed anchor), NOT around the whole
                    // shape — vertices can sit anywhere relative to it.
                    ringX = obj.position.x;
                    ringZ = obj.position.z;
                    ringInner = 0.8; ringOuter = 1.1; // same as players/balls
                }

                const ringGeo = new THREE.RingGeometry(ringInner, ringOuter, 64);
                const ringMat = new THREE.MeshBasicMaterial({
                    color: item.type === 'ball' ? 0xf59e0b
                        : item.type === 'player'
                            ? (item.side === 'home' ? 0x2563eb : 0xdc2626)
                            : item.type === 'cone' ? 0xfbbf24
                            : item.type === 'target' ? 0xef4444
                            : 0x2563eb,
                    transparent: true,
                    opacity: 0.6,
                    side: THREE.DoubleSide,
                    depthWrite: false,
                });
                const ring = new THREE.Mesh(ringGeo, ringMat);
                ring.rotation.x = -Math.PI / 2;
                ring.position.set(ringX, 0.02, ringZ);
                scene.add(ring);
                refSelectionRing.current = ring;
                refSelectionRingCenter.current.set(ringX, 0.02, ringZ);
            }
        }
    }

    // ═══════════════════════════════════════════════════════════
    // THREE.JS INITIALIZATION
    // ═══════════════════════════════════════════════════════════

    /**
     * Initialise the Three.js viewport — renderer, scene, cameras, pitch,
     * lighting, controls, post-processing, and event listeners.
     * Called once on mount via useEffect.
     */
    function initViewport3D() {
        refRenderer.current = new THREE.WebGLRenderer({antialias: true, preserveDrawingBuffer: true});
        refRenderer.current.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        refRenderer.current.shadowMap.enabled = true;
        refRenderer.current.shadowMap.type = THREE.PCFSoftShadowMap;
        refRenderer.current.toneMapping = THREE.ACESFilmicToneMapping;
        refRenderer.current.toneMappingExposure = 1.0;
        const canvas = refRenderer.current.domElement;
        canvas.style.objectFit = 'cover'; canvas.style.position = 'absolute'; canvas.style.display = 'block'; canvas.style.zIndex = '3'; canvas.style.touchAction = 'none';
        canvas.addEventListener('contextmenu', (e) => e.preventDefault());
        // Guard against 0 dimensions on initial load (CSS grid not yet settled)
        const initW = width > 0 ? width : window.innerWidth;
        const initH = height > 0 ? height : window.innerHeight;
        refRenderer.current.setSize(initW, initH);

        refPerspectiveCamera.current = new THREE.PerspectiveCamera(60, initW / initH, 0.01, 1000);
        refPerspectiveCamera.current.position.copy(defaultCameraPosition);
        refPerspectiveCamera.current.lookAt(new THREE.Vector3(0, 0, 0));
        const aspect = initW / initH;
        const vs = Math.max(55, 45 / aspect);
        refOrthoCamera.current = new THREE.OrthographicCamera(-vs * aspect / 2, vs * aspect / 2, vs / 2, -vs / 2, 0.1, 1000);
        refOrthoCamera.current.position.set(0, 80, 0.01);
        refOrthoCamera.current.lookAt(new THREE.Vector3(0, 0, 0));
        refCamera.current = refPerspectiveCamera.current;
        refScene.current = new THREE.Scene();

        refExportElement.current.appendChild(canvas);
        initGeometry(); initLighting(); initControls(); initPostProcessing();

        // Pre-allocated scratch objects for event handlers (avoids per-event allocation)
        const _evtHit = new THREE.Vector3();

        // ── Pointer ──
        const pickAt = createViewportPicker({
            getCanvas: () => canvas, getCamera: () => refCamera.current,
            getScene: () => refScene.current, raycaster,
        });
        const gesture = new PointerGesture(DRAG_THRESHOLD);
        // WHEN a near-miss may become a grab. The picker measures the distance; this
        // decides. Selecting, dragging, grouping and the tap-again menu all want the
        // item the finger aimed at, so a touch that lands within TOUCH_PICK_PX of an
        // item's own screen footprint takes it. The modes that ask for EMPTY ground or
        // for a PRECISE sub-part keep the exact ray: placing a new item must not be
        // swallowed by a cone 10 px away, and a zone vertex, a bend or arch handle, a
        // follow or attach receiver are all "exactly this thing or nothing".
        const pickForGesture = (e) => {
            const r = pickAt(e);
            if (r.root || !r.near) return r;
            if (refFollowPicking.current || refFollowTarget.current || refAttachMode.current
                || refZoneLinkTarget.current || refBendHandle.current || refArchHandle.current
                || (refActionMode.current === 'place' && refPendingObjectType.current)) return r;
            return { ...r, root: r.near };
        };
        // Touch twin of Shift+click: a long-press (finger still) on a groupable
        // item toggles its GROUP membership. Cancelled the moment a real drag
        // starts; cleared on unmount (refGroupLongPress below).
        // Green "drop here" glow on the item that would receive the drop.
        const setReceiverGlow = (obj) => {
            if (obj === refReceiverTint.current) return;
            if (refReceiverTint.current) {
                const k = dragTintKeyOfObject(refReceiverTint.current);
                clearSilhouetteTint(refReceiverTint.current);
                if (k) refGroupTinted.current.delete(k); // marker was stolen — let it re-apply later
            }
            refReceiverTint.current = obj || null;
            if (obj) {
                const k = dragTintKeyOfObject(obj);
                clearSilhouetteTint(obj);            // marker → receiver glow
                if (k) refGroupTinted.current.delete(k);
                setSilhouetteTint(obj, RECEIVER_TINT);
            }
        };
        // Current attachment target of a ball/zone track at the playhead. The one
        // implementation is carrierAt at component scope (below, beside the arrow
        // renderer) — it lives there because both the pointer UI and the renderer need
        // it, and a second copy here is how the two would drift. Keyframes only — App
        // re-checks authoritatively when the drop lands.
        const onPointerDown = (e, session) => {
            refPointerStart.current = session;
            const ownedRoot = session.root;
            if (session.itemOwned && refControls.current) refControls.current.enabled = false;
            if (refFollowPicking.current || refFollowTarget.current || refAttachMode.current || refZoneLinkTarget.current) return;

            // Grab a HANDLES first (bend / arch) — they own this pointer entirely.
            if (refBendHandle.current || refArchHandle.current) {
                const bh = pickAt(e).hits.find(h => {
                    for (let c = h.object; c; c = c.parent) if (c.userData?.isBendHandle || c.userData?.isArchHandle) return true;
                    return false;
                });
                if (bh) {
                    let c = bh.object;
                    while (c && !c.userData?.isBendHandle && !c.userData?.isArchHandle) c = c.parent;
                    const isArch = !!c.userData?.isArchHandle;
                    const bt = isArch ? c.userData.archTarget : c.userData.bendTarget;
                    if (bt) {
                        refBendDrag.current = { ...bt, kind: isArch ? 'arch' : 'bend' };
                        if (refControls.current) refControls.current.enabled = false;
                        refOnUndoStart.current?.(isArch ? 'loft height' : 'bend path');
                        e.stopPropagation();
                        return;
                    }
                }
            }


            // Grabbing a corner is not a mode. In ⇱ Move the whole shape is the
            // handle and the nearest vertex answers. In + Add the press has to
            // be ON the corner — closer than a thumb, the finger is on the
            // vertex and the intent is not a guess — and anywhere else in the
            // shape still adds, which is what that button says it does. REMOVE
            // keeps its tap-to-delete: a delete must never be a side effect of
            // a grab. This is why opening the editor and pulling a corner
            // straight away works, instead of hunting for the Move button
            // first because the default mode swallows the drag.
            if (refZoneEditOn.current && refSelectedItem.current?.type === 'zone'
                && refZoneAction.current === 'add') {
                syncZoneEditBuffer();
                const hit = pickAt(e).ground;
                const best = nearestZoneVertex(refSelectedZonePoints.current || [], hit, ZONE_GRAB_M);
                if (best !== null) {
                    setZoneSelectPoint(best);
                    refZonePointDrag.current = { zoneId: refSelectedItem.current.id, pointIndex: best, wasBound: !!refSelectedZoneRefs.current?.[best]?.ref };
                    refOnUndoStart.current?.('zone shape');
                    if (refControls.current) refControls.current.enabled = false;
                    return;
                }
            }

            // ── Direct item drag (place mode, not manual editing, not zone editing) ──
            // Works in both 3D and 2D modes.
            //
            // Any item can be dragged directly in select/place mode.
            // Special editing modes still own their existing actions.
            const selForDrag = refSelectedItem.current;
            const placeMode = refActionMode.current === 'place';
            const noneMode = refActionMode.current === 'none';
            if (!(refZoneEditOn.current && selForDrag?.type === 'zone') && (placeMode || noneMode)) {
                // Drag the exact visible item captured at touch-down.
                if (ownedRoot) {
                    refItemDrag.current = { root: ownedRoot, dragging: false, startPosition: ownedRoot.obj.position.clone() };
                    if (refControls.current) refControls.current.enabled = false;
                    if (e.pointerType === 'touch' && ['player', 'cone', 'target', 'zone', 'ball'].includes(ownedRoot.type)) {
                        session.longPressed = false;
                        clearTimeout(refGroupLongPress.current);
                        refGroupLongPress.current = setTimeout(() => {
                            if (!refPointerStart.current.active) return;
                            session.longPressed = true;
                            const it = itemDescriptor(ownedRoot);
                            if (!it) return;
                            // LONG-PRESS is a ladder. On an item you have NOT
                            // picked yet it adds it to the drag group (what
                            // shift-click does on a mouse). On an item that is
                            // ALREADY yours it opens the verb menu — the touch
                            // twin of a mouse double-click, and the only way
                            // into the action picker / attach prompt / shape
                            // editor without walking to the timeline strip.
                            const key = groupKeyOf(it);
                            const selKey = groupKeyOf(refSelectedItem.current);
                            const grouped = key && (refGroupItems.current || [])
                                .some(m => groupKeyOf(m) === key);
                            if ((key && key === selKey) || grouped) openItemVerb(it);
                            // A ball never joins a group, so its first press just
                            // picks it up; the second press opens its MENU like
                            // every other item's — 📎 attach is an entry inside
                            // it, not a separate door the ladder has to remember.
                            else if (it.type === 'ball') refOnSelectItem.current(it, false);
                            else refOnSelectItem.current(it, true);
                        }, 550);
                    }
                    return;
                }
                // Touch parity for SHIFT+CLICK sticky placement: while an
                // object type is armed, LONG-PRESS an empty spot on the pitch
                // places there AND stays armed — tap-tap-tap a line of cones
                // with one hand. A plain tap still places one and exits.
                if (!ownedRoot && e.pointerType === 'touch' && placeMode && refPendingObjectType.current) {
                    session.longPressed = false;
                    clearTimeout(refGroupLongPress.current);
                    const px = session.x, py = session.y;
                    refGroupLongPress.current = setTimeout(() => {
                        const ps = refPointerStart.current;
                        if (!ps.active || ps.dragged) return; // orbiting cancels it
                        session.longPressed = true;
                        const hit = pickAt({ clientX: px, clientY: py }).ground;
                        if (hit) refOnObjectPlace.current(refPendingObjectType.current, [hit.x, 0, hit.z], { keepArmed: true });
                    }, 550);
                    return;
                }
                // HOLD AN EMPTY SPOT in select mode → the VIEW menu. The camera
                // presets (default angle / top-down / low sideline) were 1 / 2 /
                // 3 keys and nothing else: on a phone there was no way to go
                // top-down to ARRANGE a drill, or down to the touchline to READ
                // a pass. Same long-press the items already use, on the pitch.
                if (!ownedRoot && e.pointerType === 'touch' && noneMode && refOnPitchLongPress.current) {
                    session.longPressed = false;
                    clearTimeout(refGroupLongPress.current);
                    refGroupLongPress.current = setTimeout(() => {
                        const ps = refPointerStart.current;
                        if (!ps.active || ps.dragged) return; // orbiting cancels it
                        session.longPressed = true;
                        refOnPitchLongPress.current();
                    }, 550);
                    return;
                }
            }
        };

        const onPointerMove = (e) => {
            if (e.pointerId !== refPointerStart.current.pointerId) return;
            if (!refPointerStart.current.active && !refItemDrag.current && !refZonePointDrag.current) return;

            // Live bend drag: pull the apex handle sideways — style flips to
            // the side you pull, curve amount follows the distance.
            // Live arch drag (ball): distance from the chord = arch height.
            if (refBendDrag.current) {
                const hit = pickAt(e).ground;
                if (hit) {
                    const b = refBendDrag.current;
                    const bdx = b.p1[0] - b.p0[0], bdz = b.p1[2] - b.p0[2];
                    const blen = Math.hypot(bdx, bdz);
                    const mx = (b.p0[0] + b.p1[0]) / 2, mz = (b.p0[2] + b.p1[2]) / 2;
                    if (b.kind === 'arch') {
                        const dist = Math.hypot(hit.x - mx, hit.z - mz);
                        refOnSegmentArch.current?.(b.trackId, b.from, b.to, Math.max(0.1, Math.min(10, dist)));
                    } else if (blen > 0.001) {
                        const d = (hit.x - mx) * (-bdz / blen) + (hit.z - mz) * (bdx / blen);
                        const style = d >= 0 ? 'curveRight' : 'curveLeft';
                        const curve = Math.max(0.05, Math.min(1, Math.abs(d) / (0.25 * blen)));
                        refOnSegmentBend.current?.(b.trackId, b.from, b.to, style, curve);
                    }
                }
                return;
            }


            if (refZonePointDrag.current && refSelectedZonePoints.current) {
                const hit = pickAt(e).ground;
                if (hit) {
                    _evtHit.copy(hit);
                    const { zoneId, pointIndex } = refZonePointDrag.current;
                    // Buffer is DISPLAY space — drag lands where you see it;
                    // storage converts to data space.
                    const newPoints = [...refSelectedZonePoints.current];
                    newPoints[pointIndex] = [_evtHit.x, 0, _evtHit.z];
                    // Write back immediately so the NEXT drag/add/remove chains
                    // from this state instead of reverting to the stale shape.
                    refSelectedZonePoints.current = newPoints;
                    // A grabbed attached corner must follow the pointer during
                    // the drag; its old carrier cannot keep overriding the edit.
                    const refs = [...(refSelectedZoneRefs.current || [])];
                    refs[pointIndex] = { ref: null };
                    refSelectedZoneRefs.current = refs;
                    applyZoneVisualToObject(zoneId, newPoints, refSelectedZoneRefs.current);
                    storeZonePoints(zoneId, newPoints, refSelectedZoneRefs.current);
                }
                return;
            }

            // Direct item drag
            if (refItemDrag.current) {
                const dx = e.clientX - refPointerStart.current.x, dy = e.clientY - refPointerStart.current.y;
                const dist = Math.sqrt(dx * dx + dy * dy);
                if (!refItemDrag.current.dragging && dist > DRAG_THRESHOLD) {
                    // Start dragging: select the item + open a single undo step
                    clearTimeout(refGroupLongPress.current); // a real drag kills the long-press
                    refItemDrag.current.dragging = true;
                    refPointerStart.current.dragged = true;
                    const root = refItemDrag.current.root;
                    const item = itemDescriptor(root);
                    if (item) refOnSelectItem.current(item);
                    // Silhouette glow for the whole drag — the item you are
                    // holding is unmistakable even when it overlaps others.
                    refDragTint.current = { key: dragTintKeyOfObject(root.obj), obj: root.obj };
                    clearSilhouetteTint(root.obj); // selection/group marker → full drag glow
                    setSilhouetteTint(root.obj, DRAG_TINT);
                    // GROUP DRAG: holding a member of the Shift+click group
                    // moves every member by the same delta — a formation or a
                    // whole drill shape in one gesture. Balls are excluded:
                    // their drop goes through the attach/pass flow instead.
                    const members = refGroupItems.current || [];
                    const rootKey = dragTintKeyOfObject(root.obj);
                    if (root.type !== 'ball' && members.length >= 2
                        && members.some(g => groupKeyOf(g) === rootKey)) {
                        const grp = [];
                        for (const m of members) {
                            const obj = refObjLookup.current.get(groupKeyOf(m));
                            if (!obj) continue;
                            clearSilhouetteTint(obj);            // marker → full drag glow
                            setSilhouetteTint(obj, DRAG_TINT);
                            grp.push({ ...m, obj, start: obj.position.clone() });
                        }
                        if (grp.length >= 2) refItemDrag.current.group = grp;
                    }
                    // Undo step opens AFTER the group is known, so the label
                    // tells the truth: Ctrl+Z says "move group of 4", not
                    // "move player" for what was really a formation shift.
                    if (root.type !== 'ball') refOnUndoStart.current?.(
                        refItemDrag.current.group
                          ? `move group of ${refItemDrag.current.group.length}`
                          : `move ${root.type}`);
                }
                if (refItemDrag.current.dragging) {
                    const picked = pickAt(e);
                    const pt = picked.ground;
                    if (pt) {
                        const root = refItemDrag.current.root;
                        const coords = [pt.x, 0, pt.z];
                        // Group move first: one delta, every member (the root
                        // is itself a member, so its own move call is skipped).
                        const grp = refItemDrag.current.group;
                        if (grp) {
                            const dxg = pt.x - refItemDrag.current.startPosition.x;
                            const dzg = pt.z - refItemDrag.current.startPosition.z;
                            for (const m of grp) {
                                const mCoords = [m.start.x + dxg, 0, m.start.z + dzg];
                                if (m.type === 'player' || m.side !== undefined) refOnPlayerPlace.current(m.side, m.index, mCoords);
                                // A CARRIED member gets no live writes —
                                // the carrier (possibly a group member itself)
                                // drives it every frame; committing mCoords
                                // would only litter its track with dead keys.
                                // Zones learned this first; cones and targets
                                // joined the day the sampler let them be carried.
                                else if (['zone', 'cone', 'target'].includes(m.type)
                                    && carrierAt(`${m.type}:${m.id}`)) { /* follows its carrier */ }
                                else refOnObjectMove.current(m.type, m.id, mCoords);
                            }
                        }
                        if (root.type === 'player') {
                            if (!grp) refOnPlayerPlace.current(root.obj.userData.side, root.obj.userData.playerIndex, coords);
                            // What the drag WOULD attach to if released right now,
                            // green silhouette: a ball under the cursor ("give the
                            // ball?") or ANOTHER PLAYER ("follow them?"). Same
                            // gesture, two different verbs — the drop decides.
                            const pSide = root.obj.userData.side, pIdx = root.obj.userData.playerIndex;
                            const ballHit = firstItemHit(picked.hits, r =>
                                r.type === 'ball' && carrierAt(`ball:${r.obj.userData.ballId}`) !== `${pSide}:${pIdx}`).root;
                            const mateHit = firstItemHit(picked.hits, r =>
                                r.type === 'player'
                                && `${r.obj.userData.side}:${r.obj.userData.playerIndex}` !== `${pSide}:${pIdx}`).root;
                            setReceiverGlow(ballHit ? ballHit.obj : (mateHit ? mateHit.obj : null));
                        }
                        else if (root.type === 'ball') {
                            // Preview only; no movement or attachment keys before confirmation.
                            root.obj = refObjLookup.current.get(`ball:${root.obj.userData.ballId}`) || root.obj;
                            if (refDragTint.current && refDragTint.current.obj !== root.obj) {
                                clearSilhouetteTint(refDragTint.current.obj);
                                refDragTint.current.obj = root.obj;
                                setSilhouetteTint(root.obj, DRAG_TINT);
                            }
                            refItemDrag.current.dropPosition = coords;
                            root.obj.position.set(pt.x, refItemDrag.current.startPosition.y, pt.z);
                            // Receiver glow: what the DROP will actually accept.
                            // A player (catch or pass) or a moving marker the
                            // ball can ride — a cone or a target. A ZONE is
                            // deliberately out: its footprint covers metres of
                            // pitch, so a ball let go anywhere inside one would
                            // snap to its centre by accident.
                            const recv = firstItemHit(picked.hits, r =>
                                r.type === 'player' || r.type === 'cone' || r.type === 'target').root;
                            setReceiverGlow(recv ? recv.obj : null);
                        }
                        else if (['cone', 'target', 'zone'].includes(root.type)) {
                            // One branch for the three rigid shapes that ride
                            // carriers: a cone, a target, a zone. They move the
                            // same way, preview the same way and drop the same
                            // way — one shared implementation per verb.
                            const kind = root.type;
                            const oId = kind === 'zone' ? root.obj.userData.zoneId
                                : (kind === 'cone' ? root.obj.userData.coneId : root.obj.userData.targetId);
                            const oCarrier = carrierAt(`${kind}:${oId}`);
                            if (oCarrier) {
                                // Carried item: PREVIEW ONLY (like the ball) —
                                // live movement would write base-position moves,
                                // visual-window translations and AUTO position
                                // keys that the carrier's follow logic overrides
                                // every frame. The drop routes to the
                                // attach/detach verb, which commits honestly.
                                root.obj.position.set(pt.x, root.obj.position.y, pt.z);
                            } else if (!grp) {
                                refOnObjectMove.current(kind, oId, coords);
                            }
                            // A player, ball or ANOTHER ITEM under the cursor
                            // previews attaching to it (mirrors the ball gesture).
                            // The sampler walks to any track, so the pitch offers
                            // any track — and never the thing itself.
                            const refOf = (r) => r.type === 'player'
                                ? `${r.obj.userData.side}:${r.obj.userData.playerIndex}`
                                : `${r.type}:${r.obj.userData[`${r.type}Id`]}`;
                            const recv = firstItemHit(picked.hits, r =>
                                ['player', 'ball', 'cone', 'target', 'zone'].includes(r.type)
                                && refOf(r) !== oCarrier && refOf(r) !== `${kind}:${oId}`).root;
                            setReceiverGlow(recv ? recv.obj : null);
                        }
                    }
                    return;
                }
                // Below threshold: retain item ownership without moving it
                return;
            }

            const dx = e.clientX - refPointerStart.current.x, dy = e.clientY - refPointerStart.current.y;
            if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD) refPointerStart.current.dragged = true;
        };

        const onPointerUp = (e) => {
            if (!refPointerStart.current.active || e.pointerId !== refPointerStart.current.pointerId) return;
            clearTimeout(refGroupLongPress.current);
            if (refPointerStart.current.itemOwned && refControls.current) refControls.current.enabled = true;
            // End a live bend drag (one undo step for the whole pull).
            if (refBendDrag.current) {
                refBendDrag.current = null;
                refOnUndoEnd.current?.();
                if (refControls.current) refControls.current.enabled = true;
                refPointerStart.current.active = false;
                return;
            }
            // End zone point drag
            if (refZonePointDrag.current) {
                const { zoneId, pointIndex, wasBound } = refZonePointDrag.current;
                refZonePointDrag.current = null;
                refPointerStart.current.active = false;
                if (refControls.current) refControls.current.enabled = true;
                refOnUndoEnd.current?.();
                // DRAG-TO-BIND: releasing the vertex over a player/ball attaches
                // it (same gesture language as the ball attach); releasing it
                // back over its own zone unbinds a bound vertex.
                const recv = pickAt(e, r => r.type === 'player' || r.type === 'ball' || r.type === 'zone').root;
                if (recv && (recv.type === 'player' || recv.type === 'ball')) {
                    const reference = recv.type === 'ball'
                        ? `ball:${recv.obj.userData.ballId}`
                        : `${recv.obj.userData.side}:${recv.obj.userData.playerIndex}`;
                    refOnZonePointRefChange.current?.(zoneId, pointIndex, reference);
                    if (refSelectedZoneRefs.current) {
                        const refs = [...refSelectedZoneRefs.current];
                        refs[pointIndex] = { ref: reference };
                        refSelectedZoneRefs.current = refs;
                        applyZoneVisualToObject(zoneId, refSelectedZonePoints.current, refs);
                    }
                } else if (wasBound && recv?.type === 'zone' && recv.obj.userData.zoneId === zoneId) {
                    refOnZonePointRefChange.current?.(zoneId, pointIndex, null);
                    if (refSelectedZoneRefs.current) {
                        const refs = [...refSelectedZoneRefs.current];
                        refs[pointIndex] = { ref: null };
                        refSelectedZoneRefs.current = refs;
                        applyZoneVisualToObject(zoneId, refSelectedZonePoints.current, refs);
                    }
                }
                return;
            }

            // End direct item drag
            if (refItemDrag.current) {
                const drag = refItemDrag.current;
                const wasDragging = drag.dragging;
                refItemDrag.current = null;
                // Drop the interaction glows — the drop prompt (if any) takes
                // over the signalling from here.
                const dragKey = refDragTint.current?.key || null;
                if (refDragTint.current) { clearSilhouetteTint(refDragTint.current.obj); refDragTint.current = null; }
                if (refReceiverTint.current) { clearSilhouetteTint(refReceiverTint.current); refReceiverTint.current = null; }
                if (drag.group) for (const m of drag.group) {
                    // Items are rebuilt during the drag — clear the CURRENT object.
                    const key = groupKeyOf(m);
                    clearSilhouetteTint(refObjLookup.current.get(key) || m.obj);
                    refGroupTinted.current.delete(key);
                }
                if (dragKey) refGroupTinted.current.delete(dragKey);
                applyPersistentTints(); // restore selection/group markers after the drag glow
                if (refControls.current) refControls.current.enabled = true;
                if (wasDragging) {
                    // Item was dragged — close the undo step (one undo per drag)
                    if (drag.root.type !== 'ball') refOnUndoEnd.current?.();
                    refPointerStart.current.active = false;
                    if (drag.root.type === 'ball' && drag.dropPosition) {
                        // The ball lands on a player, or RIDES a moving marker —
                        // a cone or a target. Same drop, and the glow during the
                        // drag showed exactly this set, so nothing is offered
                        // that the release will refuse.
                        const receiver = pickAt(e, r =>
                            r.type === 'player' || r.type === 'cone' || r.type === 'target').root;
                        drag.root.obj.position.copy(drag.startPosition);
                        refOnBallDrop.current?.(drag.root.obj.userData.ballId, drag.dropPosition,
                            receiver ? itemReference(receiver) : null);
                    } else if (drag.root.type === 'player') {
                        const pSide = drag.root.obj.userData.side, pIdx = drag.root.obj.userData.playerIndex;
                        // Player dropped ONTO another player → offer to follow.
                        // Checked FIRST: two overlapping bodies pick the topmost,
                        // and a ball lying at their feet must not steal a follow.
                        const mate = pickAt(e, r => r.type === 'player'
                            && `${r.obj.userData.side}:${r.obj.userData.playerIndex}` !== `${pSide}:${pIdx}`).root;
                        if (mate) {
                            refOnMarkPlayerDrop.current?.(pSide, pIdx,
                                `${mate.obj.userData.side}:${mate.obj.userData.playerIndex}`);
                        } else {
                            // Player dropped ONTO a ball → offer to hand the ball over.
                            const ballHit = pickAt(e, r => r.type === 'ball').root;
                            if (ballHit) {
                                const ballId = ballHit.obj.userData.ballId;
                                if (carrierAt(`ball:${ballId}`) !== `${pSide}:${pIdx}`) {
                                    refOnPlayerBallDrop.current?.(pSide, pIdx, ballId);
                                }
                            }
                        }
                    } else if (['zone', 'cone', 'target'].includes(drag.root.type)) {
                        // Dropped onto a player/ball → attach; dropped off its
                        // carrier onto open pitch → detach. A zone, a cone and a
                        // target all ride carriers the same way, so the release
                        // reads the same way — one shared implementation per verb.
                        const recv = pickAt(e, r => r.type === 'player' || r.type === 'ball').root;
                        const kind = drag.root.type;
                        const oId = kind === 'zone' ? drag.root.obj.userData.zoneId
                            : (kind === 'cone' ? drag.root.obj.userData.coneId
                                : drag.root.obj.userData.targetId);
                        const carrier = carrierAt(`${kind}:${oId}`);
                        const recvRef = recv
                            ? (recv.type === 'ball' ? `ball:${recv.obj.userData.ballId}`
                                : `${recv.obj.userData.side}:${recv.obj.userData.playerIndex}`)
                            : null;
                        // Free item on open pitch (null===null) stays silent —
                        // that is a plain move, already committed live. But a
                        // CARRIED item dropped on its OWN carrier must reach
                        // App's refusal ("Zone already follows Silva"), not die
                        // in this filter.
                        if (recvRef !== carrier && (recvRef !== null || carrier !== null)) {
                            if (kind === 'zone') refOnZoneDrop.current?.(oId, recvRef);
                            else refOnObjectDrop.current?.(kind, oId, recvRef);
                        }
                    }
                    return;
                }
                // Not dragged — fall through to normal click handling
            }

            if (!refPointerStart.current.active || e.pointerId !== refPointerStart.current.pointerId) return;
            const dragged = refPointerStart.current.dragged;
            refPointerStart.current.active = false;

            const root = refPointerStart.current.root;
            if (refFollowPicking.current) {
                const item = !dragged && itemDescriptor(root);
                if (item) {
                    const key = item.type === 'player' ? `player:${item.side}:${item.index}` : `${item.type}:${item.id}`;
                    refFollowTarget.current = key; setFollowTarget(key);
                    refFollowPicking.current = false; setFollowPicking(false);
                    refCameraAnim.current = null;
                }
                return;
            }
            if (refZoneLinkTarget.current && !dragged && ['player', 'ball'].includes(root?.type)) {
                const target = refZoneLinkTarget.current;
                const reference = itemReference(root);
                refOnZonePointRefChange.current?.(target.zoneId, target.pointIndex, reference);
                if (refSelectedZoneRefs.current) {
                    const refs = [...refSelectedZoneRefs.current];
                    refs[target.pointIndex] = { ref: reference };
                    refSelectedZoneRefs.current = refs;
                    applyZoneVisualToObject(target.zoneId, refSelectedZonePoints.current, refs);
                }
                setZoneLinkTarget(null);
                return;
            }
            if (refAttachMode.current && !dragged) {
                // The requester decides what is pickable. A BALL or ZONE takes a
                // player or a ball as its carrier; a PLAYER following another
                // player takes only a player, and never themselves. A GROUP
                // attach hands over the whole list: no member may become its own
                // carrier or another member's, or the sampler walks in a circle.
                const requester = refAttachMode.current;
                const isGroup = Array.isArray(requester);
                const requesters = isGroup ? new Set(requester) : new Set([requester]);
                // A group of PLAYERS is still a FOLLOW request — they mark an
                // opponent, and only a player can be marked. A group of ITEMS is
                // an attach request, which takes anything the sampler walks to.
                const isFollow = isGroup
                    ? requester.length > 0 && requester.every(r => /^(home|away):\d+$/.test(r))
                    : /^(home|away):\d+$/.test(requester);
                const ok = isFollow
                    ? root?.type === 'player' && !requesters.has(
                      `${root.obj.userData.side}:${root.obj.userData.playerIndex}`)
                    // An item rides a player, a ball, or ANOTHER ITEM. The
                    // sampler walks to any track, so a moving cone or target is
                    // a carrier a zone or ball can follow — "shadow the marker".
                    // Never the requester itself: a thing attached to itself is
                    // a loop, not a drill.
                    : ['player', 'ball', 'cone', 'target', 'zone'].includes(root?.type)
                      && !requesters.has(itemReference(root));
                if (ok) refOnAttachPlayerSelect.current?.(itemDescriptor(root));
                return;
            }

            // Zone edit mode: single click handling (add/remove)
            if (refZoneEditOn.current && refSelectedItem.current?.type === 'zone' && !dragged) {
                handleZoneEditClick(e);
                return;
            }

            if (dragged) return;
            // A long-press already toggled group membership — don't let the
            // release click re-select (which would clear the group).
            if (refPointerStart.current.longPressed) return;
            // Single click for place/select/remove (no double-click needed)
            handleSingleClick(e, root);
        };
        const onPointerCancel = (e) => {
            if (e.pointerId !== refPointerStart.current.pointerId) return;
            clearTimeout(refGroupLongPress.current);
            if (refBendDrag.current) {
                refBendDrag.current = null;
                refOnUndoEnd.current?.();
                if (refControls.current) refControls.current.enabled = true;
            }
            const drag = refItemDrag.current;
            if (drag?.root.type === 'ball') drag.root.obj.position.copy(drag.startPosition);
            if ((drag?.dragging && drag.root.type !== 'ball') || refZonePointDrag.current) refOnUndoEnd.current?.();
            refItemDrag.current = null; refZonePointDrag.current = null;
            const cancelKey = refDragTint.current?.key || null;
            if (refDragTint.current) { clearSilhouetteTint(refDragTint.current.obj); refDragTint.current = null; }
            if (refReceiverTint.current) { clearSilhouetteTint(refReceiverTint.current); refReceiverTint.current = null; }
            if (drag?.group) for (const m of drag.group) {
                const key = groupKeyOf(m);
                clearSilhouetteTint(refObjLookup.current.get(key) || m.obj);
                refGroupTinted.current.delete(key);
            }
            if (cancelKey) refGroupTinted.current.delete(cancelKey);
            applyPersistentTints();
            refPointerStart.current.active = false;
            if (refControls.current) refControls.current.enabled = true;
        };

        /**
         * Open an item's PRIMARY verb — one copy, reached two ways:
         * a mouse DOUBLE-CLICK on the item, and a touch LONG-PRESS on an item
         * that is already selected (the first long-press selects/groups it).
         *
         * Ball → attach prompt (balls have no action lane);
         * ZONE → shape/visual editor (a zone at rest has no flight to edit —
         * the action picker was the wrong card);
         * everything else, BALL INCLUDED → the item's own verb picker.
         *
         * A ball used to be special-cased straight into the attach prompt, on
         * the reasoning that "balls have no action lane". They do: a ball has
         * the same five lanes as every other item, and the strip already opens
         * this same picker for its action key. So the pitch hold was skipping
         * the one card that holds ➜ arrows, ↝ bend, ⤴ loft and ↻ again — and
         * 📎 attach is an entry inside it, wired to the very same
         * handleBallAttachPrompt the old branch called directly.
         *
         * This is the touch path into the editors that used to require the
         * timeline strip: select, then long-press again.
         */
        function openItemVerb(target) {
            refOnSelectItem.current(target, false);
            if (target.type === 'zone') {
                refOnZoneShapePrompt.current?.(target.id);
            } else {
                refOnActionPrompt.current?.(target.type === 'player'
                    ? { side: target.side, index: target.index }
                    : { trackId: `${target.type}:${target.id}` });
            }
        }

        function isAlreadyYours(target) {
            // The SAME ownership test the hold ladder uses, in one place: the item
            // you have already picked, or a member of the drag group you built.
            const sel = refSelectedItem.current;
            if (sel && sel.type === target.type) {
                if (target.type === 'player') {
                    if (sel.side === target.side && sel.index === target.index) return true;
                } else if (String(sel.id) === String(target.id)) return true;
            }
            const key = groupKeyOf(target);
            return !!(key && (refGroupItems.current || []).some(m => groupKeyOf(m) === key));
        }

        function handleSingleClick(event, root) {
            const mode = refActionMode.current;
            const item = refSelectedItem.current;
            const target = itemDescriptor(root);
            if (target) {
                if (mode === 'remove') {
                    if (target.type === 'player') refOnPlayerRemove.current(target.side, target.index);
                    else refOnObjectRemove.current(target.type, target.id);
                    return;
                }
                // A tap on a selected item keeps it selected. Empty field and
                // the panel close button are the explicit deselection actions.
                // Shift+click on a player toggles drag-group membership.
                // DOUBLE-TAP (touch) on the SAME item = fly the camera there —
                // the touch twin of click + F; mouse keeps the F key.
                const tapKey = target.type === 'player'
                    ? `p:${target.side}:${target.index}` : `${target.type}:${target.id}`;
                const tapNow = performance.now();
                const dblWindow = tapNow - refLastTap.current.t < 400 && refLastTap.current.key === tapKey;
                if (event.pointerType !== 'touch' && dblWindow) {
                    // MOUSE double-click on an item = open ITS primary verb —
                    // the 3D twin of tapping a dot in the timeline strip.
                    refLastTap.current = { t: 0, key: '' };
                    openItemVerb(target);
                    return;
                }
                if (event.pointerType === 'touch' && refLastTap.current.key === tapKey
                    && tapNow - refLastTap.current.t < 400) {
                    refLastTap.current = { t: 0, key: '' };
                    refOnSelectItem.current(target, false);
                    zoomToSelected();
                    return;
                }
                // TAP AGAIN = ITS MENU. A hold on an item that is already yours
                // opens this same card (the ladder above); a second TAP on it used
                // to do nothing at all — an inert click on the app's main door.
                // Same implementation, no 550 ms of invisible timing to learn, and
                // the same language the branch tree already uses: tap to pick it
                // up, tap again for the next verb. A fast double-tap still flies
                // the camera, because that check runs first.
                if (event.pointerType === 'touch' && isAlreadyYours(target)) {
                    refLastTap.current = { t: 0, key: '' };
                    openItemVerb(target);
                    return;
                }
                refLastTap.current = { t: tapNow, key: tapKey };
                refOnSelectItem.current(target, event.shiftKey);
                return;
            }
            const hit = pickAt(event).ground;
            // ── THE MARK SPOT ──
            // The compass is open and its ghost is already drawn on the pitch. A
            // tap on open turf now SETS the bearing and the gap where the coach is
            // pointing, instead of deselecting the very player the dial is about to
            // mark: a dial, a slider and a tap become one tap on the place they
            // mean. Nothing is written here — the dial and the ghost move to that
            // spot, and the panel's own button still commits.
            const mp = refMarkPreview.current;
            if (mp && mp.value && mp.value !== 'none' && hit) {
                refOnMarkSpotPick.current?.({ gx: hit.x, gz: hit.z });
                return;
            }
            if (mode === 'place') {
                if (!hit) return;
                const coords = [hit.x, 0, hit.z];
                if (canTapPlacePlayer(item, refPitchState.current)) refOnPlayerPlace.current(item.side, item.index, coords);
                else if (item && item.type !== 'player') moveSelectedItem(coords);
                if (!item && refPendingObjectType.current) {
                  // SHIFT places and STAYS armed — click a line of cones/targets
                  // without reopening the menu; a plain click places one and exits.
                  refOnObjectPlace.current(refPendingObjectType.current, coords, { keepArmed: !!event.shiftKey });
                }
                return;
            }
            // DOUBLE-TAP empty turf (touch) = reset the camera — the touch
            // twin of the 1 key: "show me the whole play" after zooming in.
            const turfNow = performance.now();
            if (event.pointerType === 'touch' && refLastTap.current.key === 'turf'
                && turfNow - refLastTap.current.t < 400) {
                refLastTap.current = { t: 0, key: '' };
                resetCamera();
                return;
            }
            refLastTap.current = { t: turfNow, key: 'turf' };
            refOnSelectItem.current(null, event.shiftKey);
        }

        function moveSelectedItem(coords) {
            const item = refSelectedItem.current;
            if (!item) return;
            if (item.type === 'player') refOnPlayerPlace.current(item.side, item.index, coords);
            else if (item.type === 'ball') refOnObjectMove.current('ball', item.id, coords);
            else if (item.type === 'cone') refOnObjectMove.current('cone', item.id, coords);
            else if (item.type === 'target') refOnObjectMove.current('target', item.id, coords);
            else if (item.type === 'zone') refOnObjectMove.current('zone', item.id, coords);
        }

        function handleZoneEditClick(event) {
            if (!refRenderer.current || !refCamera.current || !refScene.current) return;
            const picked = pickAt(event);
            const intersects = picked.hits;
            const action = refZoneAction.current;
            const item = refSelectedItem.current;
            if (!item || item.type !== 'zone') return;
            syncZoneEditBuffer();
            const points = refSelectedZonePoints.current ? [...refSelectedZonePoints.current] : null;

            if (action === 'remove') {
                // Only this zone is editable. An overlapping player or another
                // zone must not select a vertex from the wrong hit coordinates.
                const ownHit = firstItemHit(intersects, root =>
                    (root.type === 'zone' || root.type === 'zonePoint')
                    && root.obj.userData.zoneId === item.id);
                const idx = nearestZoneVertex(points || [], picked.ground,
                    ownHit.root ? Infinity : ZONE_GRAB_M);
                if (idx === null) return;
                if (!points || points.length <= 2) {
                    // A refusal is a door, not a wall — and silent is a wall.
                    refOnZoneVertexCommit.current?.({ kind: 'minTwo' });
                    return; // keep at least 2 vertices
                }
                const newPoints = points.filter((_, i) => i !== idx);
                // Sync pointRefs: remove the ref at the same index —
                // the OTHER refs keep their exact positions (no offset).
                const oldRefs = [...(refSelectedZoneRefs.current || [])];
                const newRefs = oldRefs.filter((_, i) => i !== idx);
                // Write back so the next edit chains from this state.
                refSelectedZonePoints.current = newPoints;
                refSelectedZoneRefs.current = newRefs;
                applyZoneVisualToObject(item.id, newPoints, newRefs);
                storeZonePoints(item.id, newPoints, newRefs);
                refOnZoneVertexCommit.current?.({ kind: 'removed', n: idx + 1, count: newPoints.length });
                return;
            }

            if (action === 'add') {
                // Pitch geometry is deliberately absent from item hits in the
                // shared picker. Use its ground plane, including outside the zone
                // and beneath players, rather than the top of a player's box.
                const pt = picked.ground;
                if (!pt) return;
                // Click + buffer both DISPLAY space; storage converts.
                const newPoint = [pt.x, 0, pt.z];
                if (!points || points.length === 0) {
                    const newRefs = [{ ref: null }];
                    refSelectedZonePoints.current = [newPoint];
                    refSelectedZoneRefs.current = newRefs;
                    applyZoneVisualToObject(item.id, [newPoint], newRefs);
                    storeZonePoints(item.id, [newPoint], newRefs);
                    refOnZoneVertexCommit.current?.({ kind: 'added', n: 1, count: 1 });
                    return;
                }
                // Insert INTO the nearest SEGMENT to the click point (same
                // display space). For each
                // edge (V_i → V_i+1) measure the true point-to-segment
                // distance, then split the winning edge at bestIdx = i + 1.
                // This keeps the new vertex between the two vertices that
                // actually bound the area nearest the click — it never lands
                // on the far side of the shape because some unrelated edge
                // has a marginally smaller distance, and it never defaults to
                // the end of the array when the click is inside the polygon.
                const zone = refPitchState.current?.zones?.find(z => z.id === item.id);
                const visual = refObjLookup.current.get(`zone:${item.id}`)?.userData?.appliedVisualValue;
                const bestIdx = zoneInsertionIndex(points, pt, visual?.closed ?? zone?.closed ?? true);
                const newPoints = [...points];
                newPoints.splice(bestIdx, 0, newPoint);
                // Sync pointRefs: insert a null ref at the same index — the
                // EXISTING refs keep their exact per-vertex positions.
                const oldRefs = [...(refSelectedZoneRefs.current || [])];
                // Ensure refs array is same length as old points
                while (oldRefs.length < points.length) oldRefs.push({ ref: null });
                oldRefs.splice(bestIdx, 0, { ref: null });
                // Write back so the next edit chains from this state.
                refSelectedZonePoints.current = newPoints;
                refSelectedZoneRefs.current = oldRefs;
                applyZoneVisualToObject(item.id, newPoints, oldRefs);
                storeZonePoints(item.id, newPoints, oldRefs);
                refOnZoneVertexCommit.current?.({ kind: 'added', n: bestIdx + 1, count: newPoints.length });
                return;
            }
        }

        refPointerCleanup.current = bindViewportPointers({
            canvas, pick: pickForGesture, gesture,
            onStart: onPointerDown, onMove: onPointerMove,
            onEnd: onPointerUp, onCancel: onPointerCancel,
        });
        refRenderer.current.setAnimationLoop(animate);
    }

    function initControls() {
        refControls.current = new OrbitControls(refCamera.current, refRenderer.current.domElement);
        refControls.current.target.set(0, 0, 0);
        applyControlsConfig(refControls.current, refNavMode.current, refIsOrtho.current, !!refFollowTarget.current);
    }

    async function initGeometry() {
        setLoadingProgress(10);
        await preloadBallModel();  // pre-load GLB so createBallObject is synchronous
        setLoadingProgress(30);
        await preloadPlayerModel(); // pre-load old GLB (fallback)
        setLoadingProgress(50);
        // Preload character assets — only styles needed by current teams
        const backendUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/api\/?$/, '');
        const { skinStyles, hairStyles } = collectNeededStyles(refHomeTeamState.current, refAwayTeamState.current);
        await preloadCharacterAssets(backendUrl, { neededSkinStyles: skinStyles, neededHairStyles: hairStyles });
        setLoadingProgress(75);
        await preloadNumberTextures(backendUrl, refHomeTeamState.current, refAwayTeamState.current);
        setLoadingProgress(90);
        refScene.current.add(await createFootballPitch(pitchProperties));
        setLoadingProgress(100);
        setAssetsLoaded(true);
        if (onAssetsLoaded) onAssetsLoaded();
    }

    function initLighting() {
        // Ambient — soft warm fill
        const ambient = new THREE.AmbientLight(0x607090, 0.35);
        refScene.current.add(ambient);

        // Hemisphere — warm sky / cool ground bounce
        const hemi = new THREE.HemisphereLight(0x80a8e0, 0x3a5a32, 0.55);
        hemi.position.set(0, 50, 0);
        refScene.current.add(hemi);

        // Main directional light (warm sunlight) with shadows
        const d = new THREE.DirectionalLight(0xfff4e0, 1.6);
        d.position.set(28, 50, 22);
        d.target.position.set(0, 0, 0);
        d.castShadow = true;
        d.shadow.mapSize.width = 1024;
        d.shadow.mapSize.height = 1024;
        d.shadow.camera.near = 0.5;
        d.shadow.camera.far = 160;
        d.shadow.camera.left = -60;
        d.shadow.camera.right = 60;
        d.shadow.camera.top = 40;
        d.shadow.camera.bottom = -40;
        d.shadow.bias = -0.0004;
        d.shadow.normalBias = 0.02;
        refScene.current.add(d);
        refScene.current.add(d.target);
        refDirLight.current = d;

        // Cool fill light from opposite side (no shadows)
        const fill = new THREE.DirectionalLight(0x80a0c0, 0.4);
        fill.position.set(-20, 30, -15);
        refScene.current.add(fill);

        // Fog for depth perception (pushed back so pitch stays clear)
        refScene.current.fog = new THREE.Fog(0x87b5e0, 120, 400);

        // Sky background + environment for reflections
        const skyTex = createSkyTexture();
        refScene.current.background = skyTex;
        refScene.current.environment = skyTex;
    }

    function initPostProcessing() {
        const composer = new EffectComposer(refRenderer.current);
        const renderPass = new RenderPass(refScene.current, refCamera.current);
        composer.addPass(renderPass);
        refRenderPass.current = renderPass;

        const bloomPass = new UnrealBloomPass(
            new THREE.Vector2(width, height),
            0.35,  // strength — subtle glow on emissive materials
            0.6,   // radius
            0.85   // threshold — only bright/emissive objects bloom
        );
        composer.addPass(bloomPass);
        composer.addPass(new OutputPass());

        refComposer.current = composer;
        refBloomPass.current = bloomPass;
    }

    // Pre-allocated scratch vectors for animation loop (avoids per-frame allocation)
    const _scratchPos = new THREE.Vector3();
    const _scratchTan = new THREE.Vector3();
    const _scratchDelta = new THREE.Vector3();
    const _scratchRollAxis = new THREE.Vector3();
    const _scratchQuat = new THREE.Quaternion();

    /**
     * V2 keyframe interpolation — drives player meshes from the timeline.
     * For every player with ≥2 position keyframes, the mesh is repositioned
     * by interpolating between the surrounding keys at the current playhead
     * time (before the first key → hold first; after the last → hold last).
     * Skipped while the user is actively placing/moving an item.
     */
    const applyKeyframeInterpolation = useCallback((delta) => {
        const t = refTimelineTime.current;
        const kf = refKeyframes.current;
        if (!kf) return;
        if (refItemDrag.current?.dragging) return; // don't fight a live item drag
        const dt = Math.max(delta || 0.016, 1e-4);
        const sel = refSelectedItem.current;
        refObjLookup.current.forEach((obj, key) => {
            if (!key.startsWith('player:')) return;
            const trackId = key.slice('player:'.length);
            const track = kf.find(k => k.id === trackId);
            const keys = track?.position || [];
            // A player FOLLOWING another player moves by a different rule: their
            // own keys are offsets and the leader's travel is added on top. The
            // sampler already knows that, so a follower goes through it — even
            // with one key or none, because the leader is what carries them.
            const following = isPlayerTrackId(trackId) && (track?.attach || []).length > 0;
            if (keys.length < 2 && !following) {
                playerTravelSpeed(obj.userData, track, t, [obj.position.x, obj.position.y, obj.position.z], dt, refPlaying.current);
                return;
            }
            // Surrounding keys (tracks are sorted by time)
            let k0 = null, k1 = null;
            for (const k of keys) {
                if (k.time <= t) k0 = k; else { k1 = k; break; }
            }
            let pos;
            if (following) {
                pos = sampleEffectivePosition(track, t, kf) || sampleTrackPosition(track, t);
            } else if (k0 && k1) {
                const span = Math.max(k1.time - k0.time, 1e-6);
                const f = (t - k0.time) / span;
                // Follow the segment trajectory (lateral curve + arch) between the pair,
                // matching the path arrows and Key Movement sampling.
                pos = interpolateWithSegment(k0, k1, f, findSegment(track, k0.time, k1.time));
            } else if (k0) {
                pos = k0.position;   // after the last key — hold final position
            } else if (k1) {
                pos = k1.position;   // before the first key — hold initial position
            } else return;
            if (!pos) return;
            obj.position.set(pos[0], (pos[1] ?? 0) + PITCH_PLACEMENT_OFFSET_Y_PLAYER, pos[2]);

            // ── Travel speed + facing (animation clips follow the movement) ──
            playerTravelSpeed(obj.userData, track, t, pos, dt, refPlaying.current);
            // (Facing is handled in its own pass below — it must run for players
            // WITHOUT a movement path too, which this loop deliberately skips.)

            // Keep the selection ring glued to the interpolated position
            if (sel && sel.type === 'player') {
                const [sid, idxStr] = key.slice('player:'.length).split(':');
                if (sid === sel.side && parseInt(idxStr, 10) === sel.index && refSelectionRing.current) {
                    refSelectionRing.current.position.set(pos[0], 0.02, pos[2]);
                }
            }
        });
        // One facing path for every player: the direction they are travelling,
        // held at rest. Close balls must not flip the body heading.
        refObjLookup.current.forEach((obj, key) => {
            if (!key.startsWith('player:')) return;
            const yaw = playerFacingYaw({
                tracks: kf, playerId: key.slice('player:'.length),
                pos: [obj.position.x, obj.position.y, obj.position.z], time: t,
                currentYaw: obj.rotation.y,
            });
            const facing = smoothPlayerYaw(obj.userData.facing, yaw, t, dt);
            obj.userData.facing = facing;
            obj.userData.yaw = facing.yaw;
            obj.rotation.y = facing.yaw;
            updatePlayerAnimation(obj, obj.userData.smoothSpeed || 0, gaitPlaybackDirection(obj.userData, facing.yaw), refPlaying.current ? 0.3 : 0.04);
        });
        // Balls: attachment-aware interpolation (follow attached players,
        // or interpolate between attachment changes / detachments)
        refObjLookup.current.forEach((obj, key) => {
            if (!key.startsWith('ball:')) return;
            // The lookup key IS the track id ('ball:ball-01') — do not strip the prefix
            const track = kf.find(k => k.id === key);
            if (!track) return;
            const hasAttach = (track.attach || []).length > 0;
            const hasPos = (track.position || []).length > 0;
            if (!hasAttach && !hasPos) return;
            const pos = sampleEffectivePosition(track, t, kf);
            if (!pos) return;
            // Keep the ball sitting on the pitch (radius ≈ 0.11m)
            obj.position.set(pos[0], Math.max(0.11, pos[1] ?? 0), pos[2]);
        });
        // Zone visual windows: apply the visual snapshot active at the playhead
        // (radius / shape points / vertex refs). Rebuilds the zone object only
        // when the window changes or its value is edited.
        refObjLookup.current.forEach((obj, key) => {
            if (!key.startsWith('zone:')) return;
            const track = kf.find(k => k.id === key);
            if (!track) return;
            const vk = (track.visual || []).slice().sort((a, b) => a.time - b.time);
            let idx = -1;
            for (let i = 0; i < vk.length; i++) { if (vk[i].time <= t) idx = i; else break; }
            const value = idx >= 0 ? (vk[idx].value || null) : null;
            const sig = value ? JSON.stringify(value) : '';
            if (obj.userData.appliedVisualSig === sig) return;
            obj.userData.appliedVisualSig = sig;
            const baseZone = refPitchState.current?.zones?.find(z => z.id === obj.userData.zoneId);
            rebuildZoneObject(obj, value, baseZone);
            // This rebuild runs AFTER syncSelectionVisual in the same frame —
            // re-apply the selection-driven cross visibility so the freshly
            // built crosses don't show/hide incorrectly (build → re-apply).
            const sel = refSelectedItem.current;
            const isSel = sel?.type === 'zone' && (
                (sel.id === obj.userData.zoneId) ||
                (obj.parent && obj.parent.userData?.zoneId === sel.id) ||
                (obj.userData.isZone && obj.userData.zoneId === sel.id)
            );
            obj.traverse((c) => { if (c.userData?.isZoneCenterCross) c.visible = isSel; });
            obj.userData.zoneCrossVisible = isSel;
        });
        // Cones / targets / zones: movement + attachment tracks drive them at
        // the playhead, exactly like the ball. Zones follow via their CENTRE
        // (the group position) so the whole shape translates together.
        refObjLookup.current.forEach((obj, key) => {
            if (!key.startsWith('cone:') && !key.startsWith('target:') && !key.startsWith('zone:')) return;
            const track = kf.find(k => k.id === key);
            if (!track) return;
            const hasAttach = (track.attach || []).length > 0;
            const hasPos = (track.position || []).length > 0;
            if (!hasAttach && !hasPos) return;
            const pos = sampleEffectivePosition(track, t, kf);
            if (!pos) return;
            obj.position.set(pos[0], pos[1] ?? 0, pos[2]);
        });
    }, []);

    /**
     * Who a track is RIDING at the playhead — 'home:3', 'ball:ball-01', or null.
     *
     * This is the ONE answer to that question in this file. It used to live inside the
     * pointer handlers; it is at component scope now because the arrow renderer needs it
     * too, and two copies of "is this thing being carried" is how a ball ends up drawing
     * an arrow that contradicts the carrier's. Reads the live refs, so it takes no
     * arguments beyond the track id.
     */
    const carrierAt = (trackId) => {
        const track = (refKeyframes.current || []).find(k => k.id === trackId);
        if (!track) return null;
        const keys = (track.attach || []).slice().sort((a, b) => a.time - b.time)
            .filter(k => k.time <= (refTimelineTime.current ?? 0));
        const active = keys.pop();
        if (active) return active.value === 'none' ? null : active.value;
        if (String(trackId).startsWith('ball:')) {
            const b = (refPitchState.current?.balls || []).find(x => x.id === trackId.slice(5));
            return b?.attached || null;
        }
        return null;
    };

    /**
     * Reconcile graphic-track path arrows with the current playhead time.
     *
     * Arrow visibility is driven by the GRAPHIC keys, as before: a track with
     * graphic keys has an active ON window (ON key → next OFF / timeline end)
     * and while the playhead is inside it we draw ONE arrow along the whole
     * window — all interpolated positions gathered between the keys. Outside
     * the window no arrow is drawn. The colour is the track default (home
     * blue / away red / ball gold) unless the window's ON key carries a
     * per-window colour override. Flow particles animate each frame.
     */
    const updateGraphicPaths = useCallback(() => {
        const scene = refScene.current;
        const kf = refKeyframes.current;
        const t = refTimelineTime.current;
        const sig = refKeyframesSig.current;
        const map = refGraphicPaths.current;
        if (!scene) return;

        // 1. Which tracks currently need a path, and for which ON-run?
        // Consecutive ON keys (no OFF between them) form ONE continuous
        // arrow covering the whole run — segments keep their own colours,
        // and the arrow STAYS drawn as the playhead crosses each key
        // (previously each window was drawn separately and the previous
        // segments vanished the moment the playhead passed a key).
        const wanted = new Map();
        for (const track of kf || []) {
            if (!track.graphic || track.graphic.length === 0) continue;
            const run = graphicRun(track.graphic, t);
            if (!run) continue;
            // A track RIDING a carrier right now has no motion of its own — its position
            // IS the carrier's position — so its arrow would restate the carrier's route on
            // top of it. Two arrows on one path read as two conflicting routes, which is
            // worst exactly when a coach is trying to see who is carrying what. The window
            // still belongs to the user: turn it on for the moments the ball travels alone
            // and it draws. carrierAt (line 1233) is the app's one answer to "who is this
            // track riding at the playhead", so this reuses it rather than re-deriving
            // attachment here.
            if (carrierAt(track.id) && !hasAttachmentFlight(track, t)) continue;
            // A trailing ON window (no OFF yet) runs to the end of the movement:
            // resolve Infinity to the last position key so the full trajectory
            // is still gathered and drawn. For attachable items (ball / zone)
            // the effective motion continues past the last position key — the
            // ball keeps following its carriers, so the window also extends to
            // the final attach key and any subsequent carrier movement. Without this, a ball
            // with ONLY attachment keys resolves to a zero-length window and
            // no arrow is drawn at all.
            const keys = track?.position || [];
            const attachKeys = track?.attach || [];
            let end = run.end;
            if (!Number.isFinite(end)) {
                const lastPosTime = keys.length > 0 ? keys[keys.length - 1].time : 0;
                const lastAttachTime = attachKeys.length > 0 ? attachKeys[attachKeys.length - 1].time : 0;
                const carrier = kf.find(tr => tr.id === attachKeys[attachKeys.length - 1]?.value);
                const carrierEnd = Math.max(0, ...(carrier?.position || []).map(k => k.time));
                const motionEnd = Math.max(lastAttachTime, lastPosTime, carrierEnd);
                end = Math.max(run.start + 0.01, Math.max(lastPosTime, motionEnd));
            }
            // Signature of the per-segment colours (used for staleness — the
            // ON keys carry optional colour overrides; the keyframes signature
            // below already catches keyframe edits, this catches colour-only
            // changes cheaply).
            const colorSig = (run.onKeys || [])
                .map(k => k.color || `def:${defaultArrowColor(track.id)}`)
                .join('|');
            wanted.set(track.id, { start: run.start, end, colorSig, onKeys: run.onKeys });
        }

        // 2. Remove stale paths (track no longer ON, or run/keys/colour changed)
        for (const [pid, entry] of map) {
            const w = wanted.get(pid);
            const stale = !w
                || entry.win.start !== w.start || entry.win.end !== w.end
                || entry.colorSig !== w.colorSig
                || entry.sig !== sig;
            if (stale) {
                scene.remove(entry.group);
                disposeGraphicPathGroup(entry.group);
                map.delete(pid);
            }
        }

        // 3. Build missing paths
        for (const [pid, w] of wanted) {
            const existing = map.get(pid);
            if (existing && existing.sig === sig
                && existing.win.start === w.start && existing.win.end === w.end
                && existing.colorSig === w.colorSig) continue;
            const track = (kf || []).find(tr => tr.id === pid);
            if (!track) continue;
            const group = buildGraphicPathGroup(track, w, kf);
            if (!group) continue;
            scene.add(group);
            map.set(pid, { group, win: { start: w.start, end: w.end }, sig, colorSig: w.colorSig });
        }

        // 4. Animate flow particles along each path (V1 style)
        const elapsed = refTimer.current ? refTimer.current.getElapsed() : 0;
        for (const { group } of map.values()) {
            const curve = group.userData.flowCurve;
            const particles = group.userData.flowParticles;
            if (!curve || !particles) continue;
            for (const p of particles) {
                let tt = (elapsed * 0.15 + p.userData.flowOffset) % 1;
                if (tt < 0) tt += 1;
                p.position.copy(curve.getPointAt(tt));
                const fade = Math.min(tt * 4, (1 - tt) * 4, 1);
                p.material.opacity = 0.3 + fade * 0.6;
            }
        }
    }, [duration]);

    /**
     * Keep the player name labels readable and honour the Names toggle.
     *
     * The labels are sprites, so they already face the camera from any orbit
     * angle; this pass handles the other half — a constant on-screen size. Each
     * label is rescaled from the pixels-per-world-unit at its own depth, so it
     * stays legible whether the camera is zoomed right in or framing the whole
     * pitch, and in both the perspective (3D) and orthographic (2D) cameras.
     * Runs every frame; ~22 labels, only a couple of vector projects each.
     */
    const updatePlayerLabels = useCallback(() => {
        const labels = refPlayerLabels.current;
        if (!labels || labels.length === 0) return;
        const on = refNamesVisible.current;
        const cam = refCamera.current;
        const canvas = refRenderer.current?.domElement;
        const hPx = canvas ? (canvas.clientHeight || canvas.height) : 0;
        // The texture's text occupies PLAYER_LABEL_FONT_PX of the canvas height,
        // so the sprite is drawn taller than the text it should end up showing.
        const spritePx = PLAYER_LABEL_TEXT_PX * (PLAYER_LABEL_CANVAS_H / PLAYER_LABEL_FONT_PX);
        for (let i = 0; i < labels.length; i++) {
            const sprite = labels[i];
            if (!sprite) continue;
            sprite.visible = on;
            if (!on) continue;
            sprite.getWorldPosition(_labelWorld); // refreshes world matrices
            const pxPerUnit = pixelsPerWorldUnitAt(cam, _labelWorld, hPx);
            if (!pxPerUnit) continue; // behind the camera — leave as-is
            const worldH = spritePx / pxPerUnit;
            sprite.scale.set(worldH * (sprite.userData.labelAspect || 4), worldH, 1);
        }
    }, []);

    function animate() {
        const timer = refTimer.current; timer.update();
        let delta = timer.getDelta(); if (delta > 0.25) delta = 0.25;

        // Timeline keyframe interpolation (player positions driven by playhead time)
        applyKeyframeInterpolation(delta);
        // Graphic-track path arrows (on/off windows → movement path)
        updateGraphicPaths();

        if (refCameraAnim.current && !(refPointerStart.current.active && refPointerStart.current.itemOwned)) {
            const a = refCameraAnim.current; a.t += delta / a.duration;
            const t = Math.min(a.t, 1);
            refCamera.current.position.lerpVectors(a.fromPos, a.toPos, easeInOut(t));
            const lookTarget = a.target || new THREE.Vector3(0, 0, 0);
            refCamera.current.lookAt(lookTarget);
            if (refControls.current) { refControls.current.target.copy(lookTarget); refControls.current.update(); }
            if (a.t >= 1) {
                if (a.swapAtEnd) {
                    const ot = refControls.current ? refControls.current.target.clone() : new THREE.Vector3(0, 0, 0);
                    refOrthoCamera.current.position.copy(refCamera.current.position);
                    refCamera.current = refOrthoCamera.current; refIsOrtho.current = true;
                    if (refRenderPass.current) refRenderPass.current.camera = refCamera.current;
                    if (refControls.current) refControls.current.dispose();
                    refControls.current = new OrbitControls(refCamera.current, refRenderer.current.domElement);
                    refControls.current.target.copy(ot);
                    applyControlsConfig(refControls.current, refNavMode.current, true, !!refFollowTarget.current);
                }
                refCameraAnim.current = null;
                if (refControls.current) refControls.current.enabled = true;
                setIsOrtho(refIsOrtho.current);
            }
        }

        // Animate selection ring pulse
        if (refSelectionRing.current) {
            refSelectionRing.current.material.opacity = 0.4 + 0.25 * Math.sin(refTimer.current.getElapsed() * 3);
        }

        // ── Single batched traverse: mixers, overlay actions, corner flags, flow particles ──
        if (refScene.current) {
            const elapsed = refTimer.current.getElapsed();
            refScene.current.traverse((c) => {
                const ud = c.userData;
                if (!ud) return;

                // Sample gestures before mixer evaluation so scrubbing and
                // repeated keys apply the exact authored full-body pose.
                if (ud.mixer) {
                    const track = (ud.side !== undefined && ud.playerIndex !== undefined)
                        ? (refKeyframes.current || []).find(tr => tr.id === `${ud.side}:${ud.playerIndex}`)
                        : null;
                    sampleCharacterAction(ud, track?.action || [], refTimelineTime.current);
                    ud.mixer.update(delta);
                }

                // 3. Corner flags (subtle sway)
                if (ud.isCornerFlag) {
                    c.rotation.z = Math.sin(elapsed * 1.5 + ud.baseAngle) * 0.08;
                }

            });

        }

        // ── Ball rolling: rotate ball based on movement direction and distance ──
        // Each frame, compute how far the ball travelled from its previous position,
        // calculate the rotation axis (perpendicular to movement direction, in the ground plane),
        // and apply a roll rotation proportional to distance / radius.
        {
            const balls = refPitchState.current?.balls || [];
            for (const ball of balls) {
                const ballId = ball?.id;
                if (!ballId) continue;
                let ballObj = refObjLookup.current.get(`ball:${ballId}`);
                if (!ballObj) continue;

                const radius = ballObj.userData?.ballRadius || 0.28;
                const rollState = refBallRollState.current.get(ballId);
                const currentPos = ballObj.position; // no clone — read directly

                if (rollState?.prevPos) {
                    _scratchDelta.set(
                        currentPos.x - rollState.prevPos.x,
                        0, // only roll on ground plane movement
                        currentPos.z - rollState.prevPos.z
                    );
                    const distance = _scratchDelta.length();
                    if (distance > 0.0001) {
                        // Roll axis = perpendicular to movement direction (cross with up)
                        _scratchRollAxis.set(_scratchDelta.z, 0, -_scratchDelta.x).normalize();
                        // Roll angle = distance / radius (radians)
                        const rollAngle = distance / radius;
                        // Apply rotation to the inner model (first child), not the wrapper
                        // The wrapper holds position; the model rotates around its center
                        const modelChild = ballObj.children.find(c => c !== ballObj.children.find(ch => ch.userData?.isShadow));
                        if (modelChild) {
                            // Use quaternion for proper axis rotation
                            _scratchQuat.setFromAxisAngle(_scratchRollAxis, rollAngle);
                            modelChild.quaternion.premultiply(_scratchQuat);
                        }
                    }
                }
                // Cache current position for next frame (clone — ballObj.position is a live reference)
                refBallRollState.current.set(ballId, { prevPos: { x: currentPos.x, y: currentPos.y, z: currentPos.z } });
            }
        }

        // Animate ground selection ring
        if (refSelectionRing.current && refSelectedObj3D.current) {
            const t = refTimer.current.getElapsed();
            const pulse = 0.6 + 0.4 * Math.sin(t * 4);
            refSelectionRing.current.material.opacity = 0.4 + pulse * 0.3;
            refSelectionRing.current.scale.setScalar(1 + pulse * 0.08);
            // For zones, center is fixed at AABB center (set in syncSelectionVisual).
            // For other items, follow the object position in case it moved.
            if (refSelectedItem.current?.type === 'zone') {
                refSelectionRing.current.position.set(
                    refSelectionRingCenter.current.x,
                    0.02,
                    refSelectionRingCenter.current.z
                );
            } else {
                refSelectionRing.current.position.set(
                    refSelectedObj3D.current.position.x,
                    0.02,
                    refSelectedObj3D.current.position.z
                );
                refSelectionRingCenter.current.set(
                    refSelectedObj3D.current.position.x,
                    0.02,
                    refSelectedObj3D.current.position.z
                );
            }
        }

        // Resolve by identity each frame: branch changes rebuild the meshes.
        if (!(refPointerStart.current.active && refPointerStart.current.itemOwned)) {
            updateFollowCamera(refFollowTarget.current, refObjLookup.current, refCamera.current, refControls.current, followScratch.current);
            if (refControls.current?.enabled) refControls.current.update();
        }

        // ── Dynamic zone deformation — AFTER animation playback so player positions are updated ──
        if (refDynamicZones.current) {
            refDynamicZones.current.forEach(z => { disposeObject3D(z); if (z.parent) z.parent.remove(z); });
        }
        refDynamicZones.current = [];
        if (refScene.current) {
            refScene.current.traverse((c) => {
                const ud = c.userData;
                if (!ud || !ud.isZone || ud.isDynamicCopy) return;
                if (ud.zoneMode !== 'dynamic') return;
                if (!Array.isArray(ud.zonePointRefs) || ud.zonePointRefs.length === 0) return;
                const staticZone = refPitchState.current?.zones?.find(z => z.id === ud.zoneId);
                const visual = ud.appliedVisualValue || staticZone || {};
                const anchor = visual.center || staticZone?.position || [0, 0, 0];
                const center = [c.position.x, 0, c.position.z];
                const newPoints = zoneDisplayPoints(ud.zonePoints, anchor, center,
                    ud.zonePointRefs, resolveZoneReference);
                const hasRef = ud.zonePointRefs.some(r => r?.ref);
                if (hasRef && newPoints.length >= 2) {
                    // Keep the unbound corners translated with the zone while
                    // attached corners follow their individual scene objects.
                    // Honour the active visual key's opacity so a key set to 0
                    // hides the deformed zone too (this copy is built from the
                    // base zone, which would otherwise always draw).
                    const dynTrack = (refKeyframes.current || []).find(k => k.id === `zone:${ud.zoneId}`);
                    const dynOpacity = zoneOpacityAt(dynTrack?.visual, refTimelineTime.current ?? 0, staticZone?.opacity ?? 0.35);
                    const newZone = createZoneObject(
                        visual.color || staticZone?.color || '#ffeb3b',
                        newPoints,
                        dynOpacity,
                        visual.closed ?? staticZone?.closed ?? true,
                        visual.thickness ?? staticZone?.thickness ?? 2,
                        { center, mode: 'dynamic' }
                    );
                    // Copy zoneId so raycaster can identify this zone for selection
                    newZone.userData.zoneId = ud.zoneId;
                    // Mark as dynamic copy so deformation loop doesn't process it again
                    newZone.userData.isDynamicCopy = true;
                    refScene.current.add(newZone);
                    refDynamicZones.current.push(newZone);
                    c.visible = false;
                } else {
                    c.visible = (visual.opacity ?? staticZone?.opacity ?? 0.35) > 0;
                }
            });
        }

        if (refZoneEditOn.current) {
            syncZoneEditBuffer();
            refScene.current?.traverse(c => {
                if (!c.userData?.isZoneActiveMarker) return;
                const point = refSelectedZonePoints.current?.[c.userData.pointIndex];
                c.visible = !!point;
                if (point) c.position.set(point[0], 0.18, point[2]);
            });
        }

        // Selection ring follows the selected item's ANIMATED position (items
        // move during playback / visual windows). For zones the ring also
        // resizes when the applied visual window changes the shape.
        if (refSelectionRing.current && refSelectedItem.current) {
            const sel = refSelectedItem.current;
            let trackObj = null;
            if (sel.type === 'zone') trackObj = refObjLookup.current.get(`zone:${sel.id}`);
            else if (sel.type === 'ball') trackObj = refObjLookup.current.get(`ball:${sel.id}`);
            else if (sel.type === 'cone') trackObj = refObjLookup.current.get(`cone:${sel.id}`);
            else if (sel.type === 'target') trackObj = refObjLookup.current.get(`target:${sel.id}`);
            else if (sel.type === 'player') trackObj = refObjLookup.current.get(`player:${sel.side}:${sel.index}`);
            if (trackObj) {
                refSelectedObj3D.current = trackObj;
                refSelectionRing.current.position.set(trackObj.position.x, 0.02, trackObj.position.z);
                refSelectionRingCenter.current.set(trackObj.position.x, 0.02, trackObj.position.z);
                // Zone ring: FIXED size, centred on the centre-cross (group
                // position). It does NOT resize to hug the shape — vertices
                // can sit anywhere relative to the anchor. (The old
                // bbox-resize branch was removed: it fought the sync's
                // 0.8/1.1 ring and re-hugged the shape on every rebuild.)
            }
        }

        // Render with post-processing (bloom if enabled, otherwise direct render)
        if (refComposer.current && bloomOn) {
            refComposer.current.render();
        } else {
            refRenderer.current.render(refScene.current, refCamera.current);
        }

        // Name labels last: camera matrices are fresh from the render above, so
        // the constant-screen-size scaling is computed against this frame.
        updatePlayerLabels();
    }

    // ═══════════════════════════════════════════════════════════
    // REFS & STATE SYNC EFFECTS
    // Keeps React refs in sync with props/state so that the
    // animation loop and event handlers always see the latest
    // values without needing to be re-created.
    // ═══════════════════════════════════════════════════════════

    // ── Resize ──
    useEffect(() => {
        if (width <= 0 || height <= 0) return; // skip until real dimensions are measured
        if (refRenderer.current) refRenderer.current.setSize(width, height);
        if (refPerspectiveCamera.current) { refPerspectiveCamera.current.aspect = width / height; refPerspectiveCamera.current.updateProjectionMatrix(); }
        if (refOrthoCamera.current) { const a = width / height; const vs = Math.max(55, 45 / a); refOrthoCamera.current.left = -vs * a / 2; refOrthoCamera.current.right = vs * a / 2; refOrthoCamera.current.top = vs / 2; refOrthoCamera.current.bottom = -vs / 2; refOrthoCamera.current.updateProjectionMatrix(); }
        if (refComposer.current) refComposer.current.setSize(width, height);
        if (refBloomPass.current) refBloomPass.current.resolution.set(width, height);
    }, [width, height]);

    // ── Shadows always on ──
    useEffect(() => {
        if (refDirLight.current) refDirLight.current.castShadow = true;
        if (refRenderer.current) refRenderer.current.shadowMap.enabled = true;
    }, []);

    // ── Nav mode (orbit / pan) — single-button interaction ──
    useEffect(() => {
        refNavMode.current = navMode;
        if (!refControls.current) return;
        applyControlsConfig(refControls.current, navMode, refIsOrtho.current, !!refFollowTarget.current);
    }, [navMode, followTarget]);

    // ── Sync prop refs ──
    useEffect(() => { refActionMode.current = actionMode; }, [actionMode]);
    useEffect(() => { refPendingObjectType.current = pendingObjectType; }, [pendingObjectType]);
    // Armed placement is STICKY — the toast fires once, then the state is
    // invisible. The canvas cursor keeps the promise visible: crosshair while
    // a pending object type waits for a field click, copy while an attach
    // pick waits for its player, cell while a bench player waits to be
    // placed. Cleared the moment the arm drops.
    useEffect(() => {
        const canvas = refRenderer.current?.domElement;
        if (!canvas) return;
        canvas.style.cursor = pendingObjectType ? 'crosshair'
          : attachMode ? 'copy'
          : actionMode === 'place' ? 'cell' : '';
    }, [pendingObjectType, attachMode, actionMode]);
    useEffect(() => { refSelectedItem.current = selectedItem; syncSelectionVisual(); applyPersistentTints(); }, [selectedItem, applyPersistentTints]);

    // Auto-disable manual edit mode when selection changes
    useEffect(() => { setZoneEditOn(false); setZoneLinkTarget(null); setZoneSelectPoint(null); }, [selectedItem?.type, selectedItem?.id]);
    useEffect(() => { refOnSelectItem.current = onSelectItem; }, [onSelectItem]);
    useEffect(() => { refOnPlayerPlace.current = onPlayerPlace; }, [onPlayerPlace]);
    useEffect(() => { refOnObjectPlace.current = onObjectPlace; }, [onObjectPlace]);
    useEffect(() => { refOnObjectMove.current = onObjectMove; }, [onObjectMove]);
    useEffect(() => { refOnPlayerRemove.current = onPlayerRemove; }, [onPlayerRemove]);
    useEffect(() => { refOnObjectRemove.current = onObjectRemove; }, [onObjectRemove]);
    useEffect(() => { refOnUndoStart.current = onUndoStart; }, [onUndoStart]);
    useEffect(() => { refOnUndoEnd.current = onUndoEnd; }, [onUndoEnd]);
    useEffect(() => { refHomeTeamState.current = homeTeamState; }, [homeTeamState]);
    useEffect(() => { refAwayTeamState.current = awayTeamState; }, [awayTeamState]);
    useEffect(() => { refZoneEditOn.current = zoneEditOn; }, [zoneEditOn]);
    useEffect(() => { refZoneAction.current = zoneAction; }, [zoneAction]);
    useEffect(() => { refOnZonePointsChange.current = onZonePointsChange; }, [onZonePointsChange]);
    useEffect(() => { refOnZonePointSelected.current = onZonePointSelected; }, [onZonePointSelected]);
    useEffect(() => { refOnZonePointRefChange.current = onZonePointRefChange; }, [onZonePointRefChange]);
    useEffect(() => { refOnAttachPlayerSelect.current = onAttachPlayerSelect; }, [onAttachPlayerSelect]);
    useEffect(() => { refOnActionPrompt.current = onActionPrompt; }, [onActionPrompt]);
    useEffect(() => { refOnZoneShapePrompt.current = onZoneShapePrompt; }, [onZoneShapePrompt]);

    // Track current zone points + per-vertex refs for the selected zone (used
    // by the zone point drag + add/remove). Prefers the APPLIED visual-window
    // shape so edit-mode operations act on the exact polygon shown at the
    // playhead. `keyframes` is a dep so edits that land in a visual key
    // (rather than pitchState) still refresh these refs.
    //
    // ── Zone display↔data offset (component-scope, shared) ──
    // A zone off-key at the playhead is REBUILT at its track-interpolated
    // centre: displayed world = stored point + D, where D = group position −
    // data anchor (zero for on-key zones). Clicks/drags arrive in world
    // (display) space while stored points stay in DATA space, so every
    // interaction converts: data = display − D.
    const computeZoneDisplayOffset = (zoneId) => {
        const zo = refObjLookup.current.get(`zone:${zoneId}`);
        if (!zo) return { dx: 0, dz: 0 };
        let anchor = null;
        const zone = refPitchState.current?.zones?.find(z => z.id === zoneId);
        const track = (refKeyframes.current || []).find(k => k.id === `zone:${zoneId}`);
        if (track?.visual?.length) {
            const tNow = refTimelineTime.current ?? 0;
            const vk = track.visual.slice().sort((a, b) => a.time - b.time);
            let vidx = -1;
            for (let i = 0; i < vk.length; i++) { if (vk[i].time <= tNow) vidx = i; else break; }
            if (vidx >= 0 && vk[vidx].value?.center) anchor = vk[vidx].value.center;
        }
        if (!anchor) anchor = zone?.position || [zo.position.x, 0, zo.position.z];
        return { dx: zo.position.x - (anchor[0] ?? 0), dz: zo.position.z - (anchor[2] ?? 0) };
    };
    /** Store edit-buffer points: buffer is DISPLAY space, storage is DATA
     *  space (data = display − D). Single conversion point for all editors. */
    const storeZonePoints = (zoneId, points, refs) => {
        const { dx, dz } = computeZoneDisplayOffset(zoneId);
        const data = (dx === 0 && dz === 0) ? points : points.map(p => [p[0] - dx, p[1] ?? 0, p[2] - dz]);
        refOnZonePointsChange.current?.(zoneId, data, refs);
    };
    const resolveZoneReference = (reference) => {
        const key = /^(home|away):/.test(reference) ? `player:${reference}` : reference;
        const obj = refObjLookup.current.get(key);
        return obj ? [obj.position.x, 0, obj.position.z] : null;
    };
    const syncZoneEditBuffer = () => {
        if (refZonePointDrag.current) return;
        const selected = refSelectedItem.current;
        const zone = selected?.type === 'zone'
            ? refPitchState.current?.zones?.find(z => z.id === selected.id) : null;
        if (!zone) {
            refSelectedZonePoints.current = null;
            refSelectedZoneRefs.current = null;
            return;
        }
        const obj = refObjLookup.current.get(`zone:${zone.id}`);
        const track = (refKeyframes.current || []).find(k => k.id === `zone:${zone.id}`);
        const active = (track?.visual || []).filter(k => k.time <= (refTimelineTime.current ?? 0))
            .sort((a, b) => a.time - b.time).pop()?.value;
        const points = active?.points || zone.points || [];
        const refs = active?.pointRefs || zone.pointRefs || [];
        const anchor = active?.center || zone.position || [0, 0, 0];
        const center = obj ? [obj.position.x, 0, obj.position.z] : anchor;
        refSelectedZonePoints.current = zoneDisplayPoints(points, anchor, center,
            zone.mode === 'dynamic' ? refs : [], resolveZoneReference);
        refSelectedZoneRefs.current = refs.map(r => ({ ref: r?.ref ?? null }));
    };
    useEffect(() => {
        syncZoneEditBuffer();
        if (selectedItem?.type === 'zone' && !pitchState?.zones?.some(z => z.id === selectedItem.id)) {
            setZoneEditOn(false);
            setZoneSelectPoint(null);
            setZoneLinkTarget(null);
        }
    }, [selectedItem, pitchState, keyframes]);
    useEffect(() => { refPitchState.current = pitchState; }, [pitchState]);

    // ── Bloom toggle ──
    useEffect(() => {
        if (refBloomPass.current) refBloomPass.current.enabled = bloomOn;
    }, [bloomOn]);



    // ── Preload team textures when team data changes ──
    // ═══════════════════════════════════════════════════════════
    // SCENE UPDATE EFFECTS
    // These effects upsert/remove 3D objects in the scene whenever
    // pitchState, team data, or edit mode changes.
    // ═══════════════════════════════════════════════════════════
    useEffect(() => {
        const preload = async () => {
            // Dynamically load any new skin/hair styles needed by the updated teams
            const backendUrl = (process.env.REACT_APP_API_URL || '/api').replace(/\/api\/?$/, '');
            const { skinStyles, hairStyles } = collectNeededStyles(homeTeamState, awayTeamState);
            await preloadCharacterAssets(backendUrl, { neededSkinStyles: skinStyles, neededHairStyles: hairStyles });

            // Preload number textures for all players
            await preloadNumberTextures(backendUrl, homeTeamState, awayTeamState);

            if (homeTeamState) await preloadTeamTextures({
                shirtFront: homeTeamState.shirtFront,
                shirtBack: homeTeamState.shirtBack,
                shortsFront: homeTeamState.shortsFront,
                shortsBack: homeTeamState.shortsBack,
                socksFront: homeTeamState.socksFront,
                gkShirtFront: homeTeamState.gkShirtFront,
                gkShirtBack: homeTeamState.gkShirtBack,
                gkShortsFront: homeTeamState.gkShortsFront,
                gkShortsBack: homeTeamState.gkShortsBack,
                gkSocksFront: homeTeamState.gkSocksFront,
            });
            if (awayTeamState) await preloadTeamTextures({
                shirtFront: awayTeamState.shirtFront,
                shirtBack: awayTeamState.shirtBack,
                shortsFront: awayTeamState.shortsFront,
                shortsBack: awayTeamState.shortsBack,
                socksFront: awayTeamState.socksFront,
                gkShirtFront: awayTeamState.gkShirtFront,
                gkShirtBack: awayTeamState.gkShirtBack,
                gkShortsFront: awayTeamState.gkShortsFront,
                gkShortsBack: awayTeamState.gkShortsBack,
                gkSocksFront: awayTeamState.gkSocksFront,
            });
        };
        preload()
            .then(() => {
                // Character meshes + textures are cached now — let the player
                // rebuild effect run so any players created before this point
                // (scenario loaded during asset preload) get their real meshes.
                setCharAssetsVersion(v => v + 1);
            })
            .catch(err => console.error('[Viewport3D] Team texture preload failed:', err));
    }, [homeTeamState, awayTeamState]);

    // ── Player scene update ──
    // Lightweight key: returns a new object identity only when deps change.
    // Avoids JSON.stringify on every render (was causing GC pressure).
    const playerDataKey = useMemo(() => ({}), [pitchState]);
    const refPlayerAppearance = useRef(null);

    useEffect(() => {
        if (!refScene.current) return;
        // ── Don't build players before the character mesh exists ──
        // A scenario can be applied while assets are still preloading (e.g. the
        // site default scenario auto-loaded at boot). Building then would create
        // players with the procedural FALLBACK body — the "loaded scenarios show
        // broken characters" bug. The loading overlay covers the viewport until
        // assets are ready, so waiting costs nothing; this effect re-runs when
        // `assetsLoaded` / `charAssetsVersion` change.
        if (!assetsLoaded && !isCharacterMeshReady()) return;
        const OFFSET = PITCH_PLACEMENT_OFFSET_Y_PLAYER;
        const previous = refPlayerAppearance.current;
        const rebuild = !previous || previous.home !== homeTeamState ||
            previous.away !== awayTeamState || previous.assets !== charAssetsVersion;
        refPlayerAppearance.current = { home: homeTeamState, away: awayTeamState, assets: charAssetsVersion };
        const toRemove = [];
        refScene.current.traverse((c) => {
            if (c?.userData?.playerInfo) {
                const { side, playerIndex } = c.userData;
                const position = pitchState?.[side]?.players?.[playerIndex]?.position;
                if (rebuild || !Array.isArray(position) || position.length < 3) toRemove.push(c);
            }
        });
        toRemove.forEach((c) => {
            if (refSelectedObj3D.current === c) refSelectedObj3D.current = null;
            if (c.userData?.playerInfo) refObjLookup.current.delete(`player:${c.userData.side}:${c.userData.playerIndex}`);
            clearSilhouetteTint(c); // dispose any glow clones before the object goes
            disposeObject3D(c);
            if (c.parent) c.parent.remove(c);
        });

        const ensureInfo = (ts, i) => { const n = String(i + 1); return (ts?.playerInfo?.[i]) || { name: `P${n}`, number: n, position: '' }; };

        const upsert = (ts, info, pos, side, index) => {
            if (!info || !pos || !Array.isArray(pos) || pos.length < 3) return;
            const name = getPlayerObjectName(info, side);
            let obj = refObjLookup.current.get(`player:${side}:${index}`);
            if (obj && !obj.parent) obj = null;

            if (!obj) {
                const playerInfo = info;
                const isGK = (playerInfo.position || '').toUpperCase() === 'GK';
                obj = createPlayerObject(info, {
                    color1: ts?.color1 ?? '#ff0000',
                    color2: ts?.color2 ?? '#000000',
                    color3: ts?.color3 ?? '#ff0000',
                    color4: ts?.color4 ?? '#000000',
                    skinStyle: playerInfo.skinStyle || 'style_000',
                    hairStyle: playerInfo.hairStyle || 'style_000',
                    isGK,
                    side,
                    moveDistance: 0,
                    numberColor: ts?.numberColor || '#ffffff',
                    teamTextures: {
                        shirtFront: ts?.shirtFront || '',
                        shirtBack: ts?.shirtBack || '',
                        shortsFront: ts?.shortsFront || '',
                        shortsBack: ts?.shortsBack || '',
                        socksFront: ts?.socksFront || '',
                        gkShirtFront: ts?.gkShirtFront || '',
                        gkShirtBack: ts?.gkShirtBack || '',
                        gkShortsFront: ts?.gkShortsFront || '',
                        gkShortsBack: ts?.gkShortsBack || '',
                        gkSocksFront: ts?.gkSocksFront || '',
                    },
                });
                obj.name = name;
                obj.userData.side = side;
                obj.userData.playerIndex = index;
                obj.traverse((c) => { if (c.isMesh) c.castShadow = true; });
                refObjLookup.current.set(`player:${side}:${index}`, obj);
                refScene.current.add(obj);
            }
            // Keep the existing mesh and animation mixer across branch boundaries.
            obj.position.set(pos[0], (pos[1] ?? 0) + OFFSET, pos[2]);
            // Face the way the player travels — the only facing rule now that
            // the look-at target feature is gone (see playerFacing.js).
            const tracks = refKeyframes.current || [];
            const time = refTimelineTime.current ?? 0;
            const initialYaw = playerFacingYaw({ tracks, playerId: `${side}:${index}`, pos, time,
                currentYaw: obj.rotation.y });
            obj.rotation.y = initialYaw;
            obj.userData.yaw = initialYaw;
            obj.userData.facing = { yaw: initialYaw, time };

        };

        // ── Off-key rebuild sampling (players) ──
        // This effect re-runs on EVERY pitchState change (including every
        // pointermove of a live drag). While a drag suspends the animate
        // loop's interpolation, re-placing players at their BASE position
        // makes any player WITHOUT a key at the current playhead jump to its
        // base/last-key spot. Sample each player's movement track at the
        // playhead instead — mirroring applyKeyframeInterpolation's gating
        // (2+ keys required). The player being dragged is skipped: its base
        // position IS the live drag position (updated per pointermove).
        const dragRoot = refItemDrag.current?.dragging ? refItemDrag.current.root : null;
        const dragPlayerKey = dragRoot?.type === 'player'
            ? `${dragRoot.obj.userData.side}:${dragRoot.obj.userData.playerIndex}`
            : null;
        const playerPosAtBuild = (side, index) => {
            if (dragPlayerKey === `${side}:${index}`) return null;
            const track = (refKeyframes.current || []).find(k => k.id === `${side}:${index}`);
            if (!track) return null;
            // Same gating as the animation loop above: a follower needs no keys
            // of its own, because the leader is what places it.
            const following = isPlayerTrackId(`${side}:${index}`) && (track.attach || []).length > 0;
            if ((track.position || []).length < 2 && !following) return null;
            return sampleEffectivePosition(track, refTimelineTime.current ?? 0, refKeyframes.current || []);
        };

        (pitchState?.home?.players ?? []).forEach((p, i) => {
            const eff = playerPosAtBuild('home', i);
            upsert(homeTeamState, ensureInfo(homeTeamState, i), eff || p?.position, 'home', i);
        });
        (pitchState?.away?.players ?? []).forEach((p, i) => {
            const eff = playerPosAtBuild('away', i);
            upsert(awayTeamState, ensureInfo(awayTeamState, i), eff || p?.position, 'away', i);
        });
        // Players may have been (re)created above — make sure group markers
        // are on the current objects.
        applyPersistentTints();

        syncSelectionVisual();

        // ── Collect the player name labels for the per-frame sizing pass ──
        // (sprites parented to the player objects, so they follow them around)
        const labels = [];
        refObjLookup.current.forEach((obj, key) => {
            if (!key.startsWith('player:')) return;
            obj.traverse((c) => { if (c.userData?.isPlayerLabel) labels.push(c); });
        });
        refPlayerLabels.current = labels;
    }, [playerDataKey, homeTeamState, awayTeamState, selectedItem, actionMode, assetsLoaded, charAssetsVersion]);

    // ── Object scene update ──
    // Lightweight key: returns a new object identity only when deps change.
    // Avoids JSON.stringify on every render (was causing GC pressure).
    const objectDataKey = useMemo(() => ({}), [pitchState]);

    useEffect(() => {
        if (!refScene.current) return;
        // ── Wait for the GLB caches before building scene objects ──
        // Balls/cones/targets use models preloaded during startup; building them
        // earlier would leave throwaway procedural placeholders behind — the same
        // race that broke characters when a scenario auto-loads at boot.
        if (!assetsLoaded) return;
        const toRemove = [];
        refScene.current.traverse((c) => { if (c?.userData?.isBall || c?.userData?.isCone || c?.userData?.isTarget || c?.userData?.isTransition || c?.userData?.isManualEditor || c?.userData?.isZone || c?.userData?.isZoneEditor || c?.userData?.isZoneActiveMarker || c?.userData?.isComboArrow) toRemove.push(c); });
        toRemove.forEach((c) => { clearSilhouetteTint(c); disposeObject3D(c); if (c.parent) c.parent.remove(c); });


        for (const key of refObjLookup.current.keys()) {
            if (!key.startsWith('player:')) refObjLookup.current.delete(key);
        }

        // Helper: compute player's position + 0.8m offset in facing direction.
        // Looks up the player's 3D object (already in scene) for rotation.y.
        // Falls back to trajectory direction (current → next-phase position).
        const computeBallPosFromPlayer = (side, idx) => {
            const players = side === 'home' ? pitchState?.home?.players : pitchState?.away?.players;
            const pCurr = players?.[idx];
            if (!pCurr?.position) return null;

            // Find the player's 3D object to get rotation.y
            const playerObj3D = refObjLookup.current.get(`player:${side}:${idx}`);

            let dirX, dirZ;
            if (playerObj3D) {
                dirX = Math.sin(playerObj3D.rotation.y);
                dirZ = Math.cos(playerObj3D.rotation.y);
            } else {
                dirX = 0; dirZ = 1;
            }

            const OFFSET = 0.8;
            const basePos = pCurr.position;
            return [basePos[0] + dirX * OFFSET, basePos[1] ?? 0, basePos[2] + dirZ * OFFSET];
        };

        const balls = pitchState?.balls ?? [];
        // The item currently being dragged (if any) — its base position IS the
        // live drag position, so track sampling is skipped for it below.
        const dragRoot = refItemDrag.current?.dragging ? refItemDrag.current.root : null;
        // Items are rebuilt on EVERY pitchState change — which is every move of
        // a live drag — so the drag glow must be re-applied to the fresh object
        // (the ball branch already re-links its preview object the same way).
        const reTintDragged = (o) => {
            if (!dragRoot || !refItemDrag.current?.dragging) return;
            const key = dragTintKeyOfObject(o);
            const dt = refDragTint.current;
            if (dt && key === dt.key) { dt.obj = o; setSilhouetteTint(o, DRAG_TINT); return; }
            // Group members are rebuilt too — keep their drag glow alive.
            const grp = refItemDrag.current.group;
            if (grp && grp.some(m => groupKeyOf(m) === key)) setSilhouetteTint(o, DRAG_TINT);
        };
        // ── Off-key rebuild sampling (balls) ──
        // Same rationale as the player/zone paths: with the animate loop
        // suspended during a drag, a rebuilt ball without a key at the
        // playhead would snap to its base/last-key spot. sampleEffectivePosition
        // resolves attachment-aware interpolated position (following the
        // attached player's interpolated track). The ball being dragged is
        // skipped — its base position is the live drag position.
        const draggedBallId = dragRoot?.type === 'ball' ? dragRoot.obj.userData.ballId : null;
        const ballPosAtBuild = (ballId) => {
            if (ballId === draggedBallId) return null;
            const track = (refKeyframes.current || []).find(k => k.id === `ball:${ballId}`);
            if (!track) return null;
            const hasAttach = (track.attach || []).length > 0;
            const hasPos = (track.position || []).length > 0;
            if (!hasAttach && !hasPos) return null;
            return sampleEffectivePosition(track, refTimelineTime.current ?? 0, refKeyframes.current || []);
        };
        balls.forEach((ball) => {
            if (!ball?.position || ball.position.length < 3) return;
            const o = createBallObject(ball.color || 0xffffff);
            o.userData.ballId = ball.id;
            o.userData.isBall = true;
            // Hidden ball: not drawn, and therefore not clickable either —
            // findUserDataRoot ignores hits on hidden objects (see above).
            o.visible = ballIsVisible(ball);
            refObjLookup.current.set(`ball:${ball.id}`, o);
            const ballAttached = ball?.attached;
            const sampled = ballPosAtBuild(ball.id);

            // Selection may rebuild the scene after the first drag movement.
            // Preview coordinates are separate from saved state until drop.
            const liveDrag = refItemDrag.current;
            const preview = ball.id === draggedBallId ? liveDrag?.dropPosition : null;
            if (preview) {
                o.position.set(preview[0], liveDrag.startPosition.y, preview[2]);
                liveDrag.root.obj = o;
            } else if (sampled) {
                // Track sample wins: interpolated or attachment-following position
                o.position.set(sampled[0], Math.max(0.11, sampled[1] ?? 0), sampled[2]);
            } else if (ballAttached) {
                // Attached to player: position at player's location + offset in facing direction
                // (per-frame loop maintains the offset during animation)
                const [attSide, attIdxStr] = ballAttached.split(':');
                const computed = computeBallPosFromPlayer(attSide, parseInt(attIdxStr));
                if (computed) {
                    o.position.set(computed[0], computed[1], computed[2]);
                } else {
                    o.position.set(ball.position[0], ball.position[1] ?? 0, ball.position[2]);
                }
            } else {
                o.position.set(ball.position[0], ball.position[1] ?? 0, ball.position[2]);
            }
            o.traverse((c) => { if (c.isMesh) c.castShadow = true; });
            // Don't ghost the ball when it's attached to a player
            refScene.current.add(o);
            reTintDragged(o);
        });
        const cones = pitchState?.cones ?? [];
        const targets = pitchState?.targets ?? [];
        // ── Cone / target scene objects ──
        // Were previously never built (state got the item, the scene got
        // nothing — "cannot place a cone/target"). Same off-key rebuild
        // sampling as balls/players: with the animate loop suspended during
        // a drag, a rebuilt item without a key at the playhead would snap
        // to its base/last-key spot. The item being dragged is skipped —
        // its base position IS the live drag position.
        const draggedConeId = dragRoot?.type === 'cone' ? dragRoot.obj.userData.coneId : null;
        const draggedTargetId = dragRoot?.type === 'target' ? dragRoot.obj.userData.targetId : null;
        const staticPosAtBuild = (trackId) => {
            const track = (refKeyframes.current || []).find(k => k.id === trackId);
            if (!track) return null;
            // A follower needs no keys of its own: the carrier is what places
            // it. Same gate the ball and the player build paths use — without
            // it an attached cone rebuilt at its authored spot mid-drill.
            if ((track.position || []).length < 2 && !(track.attach || []).length) return null;
            return sampleEffectivePosition(track, refTimelineTime.current ?? 0, refKeyframes.current || []);
        };
        cones.forEach((cone) => {
            if (!cone?.position || cone.position.length < 3) return;
            const o = createConeObject(cone.color || '#ffcc00');
            o.userData.coneId = cone.id;
            o.traverse((m) => { if (m.isMesh) m.castShadow = true; });
            const sampled = cone.id === draggedConeId ? null : staticPosAtBuild(`cone:${cone.id}`);
            const p = sampled || cone.position;
            o.position.set(p[0], p[1] ?? 0, p[2]);
            refObjLookup.current.set(`cone:${cone.id}`, o);
            refScene.current.add(o);
            reTintDragged(o);
        });
        targets.forEach((target) => {
            if (!target?.position || target.position.length < 3) return;
            const o = createTargetObject(target.color || '#ff0000');
            o.userData.targetId = target.id;
            o.traverse((m) => { if (m.isMesh) m.castShadow = true; });
            const sampled = target.id === draggedTargetId ? null : staticPosAtBuild(`target:${target.id}`);
            const p = sampled || target.position;
            o.position.set(p[0], p[1] ?? 0, p[2]);
            refObjLookup.current.set(`target:${target.id}`, o);
            refScene.current.add(o);
            reTintDragged(o);
        });

        const zones = pitchState?.zones ?? [];
        // Movement-track sample at the current playhead (used below). While an
        // item drag suspends the animate loop's track sampling, a scene
        // rebuild would otherwise place track-driven zones at their base /
        // last-key spot — every zone WITHOUT a key at the current time would
        // jump to a key position instead of holding its interpolated centre.
        // Sampling here keeps them at their interpolated position mid-drag.
        // The zone actually being dragged is skipped: its base position IS
        // the live drag position.
        const draggedZoneId = refItemDrag.current?.root?.obj?.userData?.zoneId ?? null;
        const zoneMovementAtBuild = (zoneId) => {
            if (zoneId === draggedZoneId) return null;
            const track = (refKeyframes.current || []).find(k => k.id === `zone:${zoneId}`);
            if (!track) return null;
            return sampleEffectivePosition(track, refTimelineTime.current ?? 0, refKeyframes.current || []);
        };
        // Active visual-window value for a zone at the current playhead.
        // Mirrors the animate-loop lookup: the scene-rebuild effect runs on
        // EVERY pitchState change (including every pointermove of a live
        // drag), while applyKeyframeInterpolation is suspended during drags —
        // so without this, rebuilt zones would flash back to the base
        // (placement) shape mid-drag instead of the edited shape.
        const zoneVisualAtBuild = (zoneId) => {
            const track = (refKeyframes.current || []).find(k => k.id === `zone:${zoneId}`);
            if (!track?.visual?.length) return null;
            const tNow = refTimelineTime.current ?? 0;
            const vk = track.visual.slice().sort((a, b) => a.time - b.time);
            let idx = -1;
            for (let i = 0; i < vk.length; i++) { if (vk[i].time <= tNow) idx = i; else break; }
            return idx >= 0 ? (vk[idx].value || null) : null;
        };
        zones.forEach((z) => {
            if (z?.mode === 'circle') {
                if (!z?.position) return;
                const mpos = zoneMovementAtBuild(z.id);
                const center = mpos ? [mpos[0], 0, mpos[2]] : z.position;
                const o = createZoneObject(z.color || '#ffeb3b', [], z.opacity ?? 0.35, z.closed !== false, z.thickness ?? 2, { center, mode: 'circle', radius: z.radius ?? 3 });
                o.userData.isZone = true; o.userData.zoneId = z.id; o.userData.zoneMode = 'circle'; o.userData.zonePoints = z.points || []; o.userData.zoneRadius = z.radius ?? 3;
                o.userData.isZone = true;
                const vValue = zoneVisualAtBuild(z.id);
                o.userData.appliedVisualSig = vValue ? JSON.stringify(vValue) : '';
                if (vValue) rebuildZoneObject(o, vValue, z);
                // Visual windows anchor to their captured centre; a zone that
                // is off-key at the playhead must still sit at its animated
                // spot, so slide the rebuilt group onto the track sample.
                if (mpos) o.position.set(mpos[0], 0, mpos[2]);
                // Carry the selection-driven cross flag onto the fresh object
                // (fresh crosses are built hidden; syncSelectionVisual only
                // runs on selection CHANGE, not on these rebuilds).
                const isSel = refSelectedItem.current?.type === 'zone' && refSelectedItem.current.id === z.id;
                if (isSel) o.traverse((c) => { if (c.userData?.isZoneCenterCross) c.visible = true; });
                o.userData.zoneCrossVisible = isSel;
                o.traverse((m) => { if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; } });
                refObjLookup.current.set(`zone:${z.id}`, o);
                refScene.current.add(o);
                reTintDragged(o);
                return;
            }
            if (!z?.points || z.points.length < 2) return;
            // Centre cross = the zone's position (fixed anchor set at
            // placement). Vertices are stored WORLD-space and drawn relative
            // to this anchor — they may sit anywhere around it. When a
            // movement track has this zone off-key at the playhead, build at
            // the track's interpolated centre (and slide the base points with
            // it) so mid-drag rebuilds don't teleport the zone to a key spot.
            const mposP = zoneMovementAtBuild(z.id);
            const centerP = mposP ? [mposP[0], 0, mposP[2]] : (z.position || [0, 0, 0]);
            const ptsP = mposP && z.position
                ? z.points.map(p => [p[0] + (mposP[0] - z.position[0]), p[1] ?? 0, p[2] + (mposP[2] - z.position[2])])
                : z.points;
            const o = createZoneObject(z.color || '#ffeb3b', ptsP, z.opacity ?? 0.35, z.closed !== false, z.thickness ?? 2, { center: centerP, mode: z.mode || 'static' });
            o.userData.isZone = true; o.userData.zoneId = z.id; o.userData.zoneMode = z.mode || 'static'; o.userData.zonePointRefs = z.pointRefs || []; o.userData.zonePoints = z.points;
            const vValue = zoneVisualAtBuild(z.id);
            o.userData.appliedVisualSig = vValue ? JSON.stringify(vValue) : '';
            if (vValue) rebuildZoneObject(o, vValue, z);
            // Same off-key slide for polygon zones with visual windows.
            if (mposP) o.position.set(mposP[0], 0, mposP[2]);
            // Carry the selection-driven cross flag onto the fresh object
            const isSelP = refSelectedItem.current?.type === 'zone' && refSelectedItem.current.id === z.id;
            if (isSelP) o.traverse((c) => { if (c.userData?.isZoneCenterCross) c.visible = true; });
            o.userData.zoneCrossVisible = isSelP;
            // Zones don't cast shadows — they're overlay markers, not solid objects
            o.traverse((m) => { if (m.isMesh) { m.castShadow = false; m.receiveShadow = false; } });
            refObjLookup.current.set(`zone:${z.id}`, o);
            refScene.current.add(o);
            reTintDragged(o);
        });

        // Items were (re)created above — group markers belong on the fresh objects.
        applyPersistentTints();

        // Always clear animation curves — even when not in transition mode (last phase)
        // so stale curves from a previous phase don't cause the ball-follows-player
        // code to move the ball along an old curve when the player has no curve.
        refBallRollState.current = new Map(); // reset ball rolling state (prevents jump rotations)


        // Zone edit: small flat marker at the ACTIVE vertex only. The old V1
        // sphere overlay is gone (it rendered from stale base data and never
        // matched the shape being edited) — the zone polygon itself is the
        // grab surface via nearest-vertex snapping. The marker simply shows
        // which vertex is currently selected/attached.
        if (zoneEditOn && selectedItem?.type === 'zone' && zoneSelectPoint !== null) {
            const zone = (pitchState?.zones ?? []).find(z => z.id === selectedItem.id);
            let editPoints = zone?.points || null;
            if (zone) {
                const zo = refObjLookup.current.get(`zone:${zone.id}`);
                if (zo && zo.userData.appliedVisualValue) {
                    editPoints = zo.userData.appliedVisualValue.points || zone.points || null;
                }
            }
            const pt = editPoints && editPoints[zoneSelectPoint];
            if (pt) {
                // editPoints are anchor-frame (data/captured); the marker sits
                // in the SCENE, so it needs the display offset added.
                const { dx: mdx, dz: mdz } = computeZoneDisplayOffset(selectedItem.id);
                const marker = new THREE.Mesh(
                    new THREE.SphereGeometry(0.16, 16, 16),
                    new THREE.MeshBasicMaterial({ color: zone?.color ? new THREE.Color(zone.color) : 0xffeb3b })
                );
                marker.position.set(pt[0] + mdx, 0.18, pt[2] + mdz);
                // Flag for cleanup: without this the marker is NEVER removed by
                // the traverse above, so every edit run stacks another sphere —
                // the "duplicate points" bug (old markers stayed visible).
                marker.userData.isZoneEditor = true;
                marker.userData.isZoneActiveMarker = true;
                marker.userData.pointIndex = zoneSelectPoint;
                refScene.current.add(marker);
            }
        }

        syncSelectionVisual();

    }, [objectDataKey, selectedItem, zoneEditOn, keyframes, zoneSelectPoint, assetsLoaded]);

    // ── Init / cleanup ──
    useEffect(() => {
        initViewport3D();
        return () => {
            refPointerCleanup.current?.(); refPointerCleanup.current = null;
            clearTimeout(refGroupLongPress.current);
            if (refControls.current) { refControls.current.dispose(); refControls.current = null; }
            if (refComposer.current) { refComposer.current.dispose(); refComposer.current = null; }
            if (refRenderer.current) { refRenderer.current.setAnimationLoop(null); refRenderer.current.dispose(); if (refRenderer.current.domElement.parentNode) refRenderer.current.domElement.parentNode.removeChild(refRenderer.current.domElement); refRenderer.current = null; }
            refCamera.current = null; refScene.current = null;
        };
    }, []);

    // ═══════════════════════════════════════════════════════════
    // UI RENDER — JSX
    // Action buttons, camera toggles, animation controls,
    // left/right panels, manual-path & zone editors, screenshot.
    // ═══════════════════════════════════════════════════════════

    // (removed dead actionBtns — the Place / Remove / Deselect array was never rendered: the
    // pitch verbs live in each item's Action menu, where the label says what it does, and
    // leaving this array behind meant three English strings no user could ever reach.)

    return (
        <div ref={refExportElement} style={{width, height, position: 'relative', background: '#000'}}>
            {followPicking && <div role="status" style={{ position:'absolute', bottom:16, left:'50%', transform:'translateX(-50%)', zIndex:100, background:'#0f172a', color:'#fff', padding:12, borderRadius:8, maxWidth:'90%', textAlign:'center' }}>
                {t('view.pickHint')}
                <button type="button" onClick={() => { refFollowPicking.current = false; setFollowPicking(false); }}>{t('confirm.cancel')}</button>
            </div>}
            {/* ── Loading overlay — shown until all 3D assets are ready ── */}
            {!assetsLoaded && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: '#0f172a', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center', gap: '1.5rem',
                }}>
                    <img src="/logo-nav.png" alt="KickViz" style={{ width: '120px', height: 'auto', opacity: 0.9 }} />
                    <div style={{ width: 'min(280px, 70vw)', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                        <div style={{ width: '100%', height: '6px', background: '#1e293b', borderRadius: '3px', overflow: 'hidden' }}>
                            <div style={{
                                width: `${loadingProgress}%`, height: '100%',
                                background: 'linear-gradient(90deg, #2563eb, #38bdf8)',
                                borderRadius: '3px', transition: 'width 0.3s ease',
                            }} />
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ color: '#64748b', fontSize: '11px' }}>{t('vp.loading')}</span>
                            <span style={{ color: '#38bdf8', fontSize: '13px', fontWeight: '700' }}>{loadingProgress}%</span>
                        </div>
                    </div>
                </div>
            )}
            {/* ── Camera navigation (kept from V1) — hovers over the viewport ── */}
            {assetsLoaded && (
              <div className="vp-nav" style={{
                position: 'absolute', top: '10px', right: '10px', zIndex: 30,
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                {/* Player name labels on/off */}
                <button
                  type="button"
                  className="vp-nav-names"
                  onClick={handleToggleNames}
                  title={t('view.namesTitle')}
                  aria-label={t('view.names')}
                  aria-pressed={namesVisible}
                  style={{
                    ...camNavBtn,
                    background: namesVisible ? '#2563eb' : 'rgba(30,41,59,0.92)',
                    opacity: namesVisible ? 1 : 0.75,
                  }}
                >
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                    <circle cx="7" cy="7" r="1.4" />
                  </svg>

                </button>
                {/* 2D / 3D view toggle */}
                <button
                  type="button"
                  onClick={toggleCamera}
                  title={isOrtho ? t('view.3d') : t('view.2d')}
                  aria-label={isOrtho ? t('view.3d') : t('view.2d')}
                  aria-pressed={isOrtho}
                  style={{
                    ...camNavBtn, background: isOrtho ? '#2563eb' : 'rgba(30,41,59,0.92)',
                  }}
                >
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 2l9 5v10l-9 5-9-5V7l9-5z" /><path d="M3 7l9 5 9-5" /><path d="M12 12v10" />
                  </svg>
                </button>
                {/* Pan / Orbit segmented control */}
                <div style={{
                  display: 'flex', borderRadius: '4px', overflow: 'hidden',
                  border: '1px solid #475569', background: 'rgba(30,41,59,0.92)',
                }}>
                  <button
                    type="button"
                    onClick={() => { setNavMode('orbit'); }}
                    title={t('view.orbit')}
                    aria-label={t('view.orbit')}
                    aria-pressed={!followTarget && !followPicking && navMode === 'orbit'}
                    style={{
                      ...camNavBtn, borderRadius: 0, border: 'none',
                      background: !followTarget && !followPicking && navMode === 'orbit' ? '#2563eb' : 'transparent',
                    }}
                  >
                    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 12a9 9 0 1 1-9-9" /><polyline points="21 3 21 9 15 9" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => { stopFollow(); setNavMode('pan'); }}
                    title={t('view.pan')}
                    aria-label={t('view.pan')}
                    aria-pressed={!followTarget && !followPicking && navMode === 'pan'}
                    style={{
                      ...camNavBtn, borderRadius: 0, border: 'none',
                      borderLeft: '1px solid #475569',
                      background: !followTarget && !followPicking && navMode === 'pan' ? '#2563eb' : 'transparent',
                    }}
                  >
                    <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 11V6a2 2 0 0 0-4 0v5" /><path d="M14 10V4a2 2 0 0 0-4 0v6" />
                      <path d="M10 10.5V6a2 2 0 0 0-4 0v8" />
                      <path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
                    </svg>
                  </button>
                </div>
                <button type="button" aria-pressed={!!followTarget || followPicking} title={t('view.followTitle')} aria-label={t('view.follow')} style={{ ...camNavBtn, background: followTarget || followPicking ? '#2563eb' : 'rgba(30,41,59,0.92)' }} onClick={() => { refFollowPicking.current = true; setFollowPicking(true); }}>
                  <svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <circle cx="12" cy="12" r="4" /><path d="M12 2v3m0 14v3M2 12h3m14 0h3M6 3H3v3m15-3h3v3M3 18v3h3m12 0h3v-3" />
                  </svg>
                </button>
                {/* Zoom to selected item */}
                <button
                  type="button"
                  onClick={zoomToSelected}
                  title={t('view.zoomTo')}
                  aria-label={t('view.zoomTo')}
                  style={camNavBtn}
                >
                  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    <line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" />
                  </svg>
                </button>
              </div>
            )}

            {/* ── Zone edit-mode panel — Add / Move / Remove polygon points ── */}
            {zoneEditOn && selectedItem?.type === 'zone' && (
              <div className="kv-zone-edit-panel" style={{
                position: 'absolute', top: '52px', right: '10px', zIndex: 31,
                background: 'rgba(15,23,42,0.96)', border: '1px solid #7c3aed', borderRadius: 10,
                padding: '8px 10px', boxShadow: '0 6px 24px rgba(0,0,0,0.45)',
                display: 'flex', flexDirection: 'column', gap: 6, color: '#e2e8f0',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, color: '#c4b5fd', letterSpacing: 0.4 }}>{t('zone.edit')}</span>
                  <button type="button" className="kv-zone-edit-exit" title={t('zone.exitEditHint')}
                    onClick={() => { setZoneLinkTarget(null); setZoneEditOn(false); if (onZoneEditExit) onZoneEditExit(); }}
                    style={{ width: 20, height: 20, borderRadius: '50%', border: 'none', background: '#dc2626', color: '#fff', fontSize: 12, lineHeight: '18px', cursor: 'pointer', fontWeight: 700, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    ✕
                  </button>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {/* TWO buttons, not three. "⇱ Move" used to be a mode you had
                      to switch into before a corner would budge — but pressing a
                      corner already grabs it in Add mode, so the mode existed to
                      hide a tap, and its click did nothing at all. */}
                  {['add', 'remove'].map(a => (
                    <button key={a} type="button"
                      onClick={() => setZoneAction(a)}
                      title={a === 'add' ? t('zone.add') : t('zone.remove')}
                      style={{
                        flex: 1, padding: '6px 10px', fontSize: 12, fontWeight: 700, borderRadius: 6, cursor: 'pointer',
                        border: zoneAction === a ? '1px solid #8b5cf6' : '1px solid #475569',
                        background: zoneAction === a ? '#4c1d95' : '#1e293b', color: zoneAction === a ? '#fff' : '#cbd5e1',
                      }}>
                      {a === 'add' ? t('zone.add') : t('zone.remove')}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 10, color: '#94a3b8', lineHeight: 1.35 }}>
                  {zoneAction === 'add' && t('zone.addHint')}
                  {zoneAction === 'remove' && t('zone.removeHint')}
                </div>
              </div>
            )}
            {/* ── Zone vertex link mode instruction — "tap a scene item" ──
                The bar used to say "press Esc / click ✕ to cancel" and there was
                no ✕ anywhere in it: the only real exit was a key a phone does not
                have, or changing the selection somewhere else on the pitch. A mode
                that announces its own way out has to ship one, and it has to be a
                move a thumb can make. */}
            {zoneLinkTarget && (
              <div className="kv-zone-link-bar" style={{
                position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 35,
                background: 'rgba(76,29,149,0.95)', border: '1px solid #8b5cf6', borderRadius: 8,
                padding: '8px 14px', color: '#ede9fe', fontSize: 13, fontWeight: 600,
                boxShadow: '0 6px 24px rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', gap: 8,
                maxWidth: '90%',
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#c4b5fd" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t('zone.linkBar')}</span>
                <button type="button" className="kv-zone-link-cancel" title={t('zone.linkCancel')}
                  onClick={() => { setZoneLinkTarget(null); }}
                  style={{
                    flexShrink: 0, width: 24, height: 24, borderRadius: '50%', border: 'none',
                    background: '#dc2626', color: '#fff', fontSize: 13, lineHeight: '22px', cursor: 'pointer',
                    fontWeight: 700, padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  ✕
                </button>
              </div>
            )}

            {/* Master playback and undo/redo stay available when timeline panels are hidden. */}
            <div style={{
              position: 'absolute', bottom: '10px', left: '10px', zIndex: 32,
              display: 'flex', gap: '6px',
            }}>
              <MasterPlayButton
                playing={masterPlaying}
                onPlay={onMasterPlay}
                speed={playbackSpeed}
                onSpeedChange={onPlaybackSpeedChange}
                label={masterPlaying ? t('tl.pause') : t('branch.masterPlay')}
                style={{ ...camNavBtn, width: 40, height: 40, justifyContent: 'center', padding: 0, borderRadius: 8, background: masterPlaying ? '#2563eb' : 'rgba(30,41,59,0.92)' }}
              />
              <NextBranchPlayButton
                playing={nextBranchPlaying}
                onPlay={onNextBranchPlay}
                label={nextBranchPlaying ? t('tl.pause') : t('branch.playToNext')}
                style={{ ...camNavBtn, width: 40, height: 40, justifyContent: 'center', padding: 0, borderRadius: 8, background: nextBranchPlaying ? '#2563eb' : 'rgba(30,41,59,0.92)' }}
              />
              <button
                type="button"
                onClick={() => onUndo?.()}
                disabled={!canUndo}
                title={canUndo
                  ? t(undoCount === 1 ? 'vp.undoHintOne' : 'vp.undoHintMany', {
                      what: undoLabel ? t('vp.undoWhat', { what: undoLabel }) : t('undo.title'),
                      n: undoCount, limit: undoAtLimit ? t('vp.undoLimit') : '',
                    })
                  : t('undo.title')}
                style={{
                  ...camNavBtn, width: 40, height: 40, justifyContent: 'center', padding: 0,
                  borderRadius: 8, opacity: canUndo ? 1 : 0.35, cursor: canUndo ? 'pointer' : 'default',
                }}
                aria-label={t('undo.title')}
              >
                <UndoIcon />
              </button>
              <button
                type="button"
                onClick={() => onRedo?.()}
                disabled={!canRedo}
                title={canRedo
                  ? t(redoCount === 1 ? 'vp.redoHintOne' : 'vp.redoHintMany', {
                      what: redoLabel ? t('vp.redoWhat', { what: redoLabel }) : t('redo.title'),
                      n: redoCount,
                    })
                  : t('redo.title')}
                style={{
                  ...camNavBtn, width: 40, height: 40, justifyContent: 'center', padding: 0,
                  borderRadius: 8, opacity: canRedo ? 1 : 0.35, cursor: canRedo ? 'pointer' : 'default',
                }}
                aria-label={t('redo.title')}
              >
                <RedoIcon />
              </button>
            </div>

            <CaptureControl
              open={captureOpen}
              setOpen={onCaptureOpenChange}
              getCanvas={() => refRenderer.current?.domElement}
              takeScreenshot={doScreenshot}
              recordToggleTick={recordToggleTick}
              onNotify={onCaptureNotify}
            />
                    </div>
    );
}

// ── Camera navigation button style (shared) ──
const camNavBtn = {
  background: 'rgba(30,41,59,0.92)', color: '#e2e8f0', border: '1px solid #475569',
  borderRadius: '4px', padding: '5px 10px', cursor: 'pointer', fontSize: '11px',
  fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px',
  whiteSpace: 'nowrap',
};

// ═══════════════════════════════════════════════════════════════════════
// GRAPHIC-TRACK PATH HELPERS
//
// The graphic track is an on/off switch. At any time T:
//   1. Find the nearest graphic key with time <= T. If it is OFF (or none
//      exists) → no path for this player.
//   2. If it is ON → the path window starts at that key's time and ends at
//      the NEXT OFF key (or the end of the timeline).
//   3. Sample the player's interpolated movement position at every whole
//      second inside the window and draw a V1-style transition arrow
//      (glowing tube + arrowhead + flow particles) along those points.
// ═══════════════════════════════════════════════════════════════════════

/**
 * The contiguous ON-run containing the playhead, or null.
 *
 * Consecutive ON keys with no OFF between them belong to the SAME arrow:
 * returns the whole run { start, end, onKeys } so the path is drawn as one
 * continuous multi-coloured line and stays visible as the playhead crosses
 * each key. `end` is the first OFF after the run, or Infinity for a trailing
 * ON run.
 *
 * @param {Array} graphicKeys - [{ time, value: 'on'|'off', color? }]
 * @param {number} t - playhead time (seconds)
 * @returns {{start:number, end:number, onKeys:Array}|null}
 */
function graphicRun(graphicKeys, t) {
    if (!graphicKeys || graphicKeys.length === 0) return null;
    const sorted = [...graphicKeys].sort((a, b) => a.time - b.time);
    let nearest = null;
    for (const k of sorted) {
        if (k.time <= t) nearest = k; else break;
    }
    if (!nearest || nearest.value !== 'on') return null;
    const idx = sorted.indexOf(nearest);
    // Walk back over consecutive ON keys → run start.
    let start = nearest.time;
    let i = idx;
    while (i > 0 && sorted[i - 1].value === 'on') {
        i--;
        start = sorted[i].time;
    }
    // Walk forward: the run ends at the first OFF after the last ON.
    let end = Infinity;
    i = idx;
    while (i + 1 < sorted.length) {
        const nk = sorted[i + 1];
        if (nk.value === 'off') { end = nk.time; break; }
        i++;
    }
    const onKeys = sorted.filter(k => k.value === 'on'
        && k.time >= start && (Number.isFinite(end) ? k.time < end : true));
    return { start, end, onKeys };
}

/**
 * Rebuild a zone object's children from a visual-window value (or the base
 * zone). Used when the playhead crosses a zone visual key or the window's
 * value is edited — the shape/radius/refs change without a React rebuild.
 * The group keeps its current position (the animated centre).
 */
function rebuildZoneObject(obj, value, baseZone) {
    const b = baseZone || {};
    const color = value?.color || b.color || '#ffeb3b';
    const opacity = value?.opacity ?? b.opacity ?? 0.35;
    const closed = value?.closed !== undefined ? value.closed : (b.closed !== false);
    const thickness = value?.thickness ?? b.thickness ?? 2;
    const mode = b.mode || 'static';
    const radius = value?.radius ?? b.radius ?? 3;
    const points = value?.points || b.points || [];
    const refs = value?.pointRefs || b.pointRefs || [];

    // Dispose existing children and rebuild in place
    while (obj.children.length > 0) {
        const ch = obj.children[obj.children.length - 1];
        obj.remove(ch);
        disposeObject3D(ch);
    }
    // Anchor: the point set being drawn is WORLD-space, captured against a
    // centre (value.center for visual windows, the zone's base position for
    // the base shape). Drawing local = point − anchor keeps the captured FORM
    // intact, and the group's (animated) position then translates the whole
    // shape with the zone — dragging never resets or re-forms the shape.
    const valuePts = Array.isArray(value?.points) ? value.points : null;
    // Anchor: the centre the point set was captured against (value.center,
    // stored when the key was created). The drawn shape then translates with
    // the group's animated position (world = point + groupPos − center), so
    // the polygon stays GLUED to the centre cross at every playhead state —
    // on-key, off-key, mid-drag — while preserving the captured form. Legacy
    // keys without a stored centre fall back to the base position.
    const anchor = valuePts
        ? (value.center || b.position || [obj.position.x, 0, obj.position.z])
        : (b.position || [obj.position.x, 0, obj.position.z]);
    const center = [anchor[0] ?? 0, 0, anchor[2] ?? 0];
    const fresh = createZoneObject(color, points, opacity, closed, thickness, { center, mode, radius });
    // Visibility follows createZoneObject's rule (opacity 0 → zone hidden for
    // this key's window); the group is rebuilt in place, so carry the flag over.
    obj.visible = fresh.visible;
    while (fresh.children.length > 0) {
        const ch = fresh.children[0];
        fresh.remove(ch);
        obj.add(ch);
    }
    fresh.children.length = 0; // clear any remaining (should be none)
    disposeObject3D(fresh);

    // Re-apply the centre-cross visibility flag — this rebuild runs from the
    // animate loop AFTER syncSelectionVisual, so fresh crosses (built hidden)
    // must inherit the zone's current selection state or they'd vanish.
    obj.traverse((c) => { if (c.userData?.isZoneCenterCross) c.visible = !!obj.userData.zoneCrossVisible; });

    // Keep the applied data for the dynamic-deformation loop + selection
    obj.userData.zoneMode = mode;
    obj.userData.zonePoints = points;
    obj.userData.zonePointRefs = refs;
    obj.userData.zoneRadius = radius;
    // Full snapshot of what is currently displayed — the zone edit points and
    // the point/ref sync use this (NOT the base phase) while editing.
    obj.userData.appliedVisualValue = {
        center,
        points: points.map(p => [...p]),
        pointRefs: refs.map(r => ({ ref: r?.ref ?? null })),
        radius, closed, color, opacity, thickness,
    };
}

/**
 * Opacity in effect for a zone at time t: the active visual key's opacity when
 * a key applies, otherwise the zone's base opacity. 0 hides the zone.
 *
 * Used where a zone is drawn from the base zone instead of a key window (the
 * dynamic-zone deformation copies rebuilt every frame), so `opacity: 0` on a
 * key hides those too instead of being ignored.
 *
 * @param {Array} visualKeys - the zone track's visual keys
 * @param {number} t - playhead time
 * @param {number} baseOpacity - the zone's base opacity
 * @returns {number} opacity to draw with
 */
function zoneOpacityAt(visualKeys, t, baseOpacity) {
    const vk = (visualKeys || []).slice().sort((a, b) => a.time - b.time);
    let active = null;
    for (const k of vk) { if (k.time <= t) active = k; else break; }
    return active?.value?.opacity ?? baseOpacity;
}

/** Dispose every mesh/material inside a path group. */
function disposeGraphicPathGroup(group) {
    group.traverse((o) => {
        if (o.geometry) o.geometry.dispose();
        if (o.material) {
            if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
            else o.material.dispose();
        }
    });
}

/**
 * Default transition-arrow colours per track type.
 *   Home: blue · Away: red · Ball: golden yellow · others: purple
 */
const DEFAULT_ARROW_COLORS = {
    home: 0x3b82f6,
    away: 0xef4444,
    ball: 0xf59e0b,
    cone: 0xa78bfa,
    target: 0xa78bfa,
    zone: 0xa78bfa,
};

/** Default arrow colour for a track id ('home:0', 'away:3', 'ball:ball-1', …). */
function defaultArrowColor(trackId) {
    for (const [prefix, color] of Object.entries(DEFAULT_ARROW_COLORS)) {
        if (trackId.startsWith(`${prefix}:`)) return color;
    }
    return 0xa78bfa;
}

/**
 * True polyline curve through dense samples: straight segments between
 * consecutive points. With 0.25s sampling the arcs render smooth while
 * direction changes stay SHARP at keyframes (three's 'linear' CatmullRom
 * returns zero vectors in r184, so we roll our own).
 */
class PolylineCurve extends THREE.Curve {
    constructor(points) {
        super();
        this.points = points;
    }
    getPoint(t, optionalTarget = new THREE.Vector3()) {
        const pts = this.points;
        const l = pts.length;
        if (l === 0) return optionalTarget.set(0, 0, 0);
        if (l === 1) return optionalTarget.copy(pts[0]);
        const p = (l - 1) * Math.min(Math.max(t, 0), 1);
        const i = Math.min(Math.floor(p), l - 2);
        const w = p - i;
        return optionalTarget.copy(pts[i]).lerp(pts[i + 1], w);
    }
}

/**
 * Build the V1-style transition arrow for an ON-RUN of a track's graphic
 * track. The run may span MULTIPLE consecutive ON keys — each inter-key
 * segment is drawn as its own tube in its own colour (the ON key's colour
 * override, else the track default), joined into ONE continuous path with a
 * single start dot and a single arrowhead at the run's end. Returns null
 * when nothing moves.
 *
 * The path is a dense polyline (0.25s samples), so arcs render smoothly while
 * direction changes stay SHARP at keyframes.
 *
 * @param {object} track - keyframe track ({ id, position: [...], graphic: [...] })
 * @param {{start:number, end:number, onKeys:Array}} run - resolved ON-run
 * @param {Array} allTracks - full keyframe track list (for attach-aware sampling)
 */
function buildGraphicPathGroup(track, run, allTracks) {
    const start = run.start;
    const end = Number.isFinite(run.end) ? run.end : start;
    const onKeys = run.onKeys || [];

    // Segment bounds: [key_i.time, key_{i+1}.time] for all but the last ON
    // key, then [lastOnKey.time, runEnd] for the final segment.
    const segBounds = [];
    for (let i = 0; i < onKeys.length; i++) {
        const a = onKeys[i].time;
        const b = i + 1 < onKeys.length ? onKeys[i + 1].time : end;
        if (b <= a) continue;
        const color = onKeys[i].color
            ? parseInt(String(onKeys[i].color).replace('#', ''), 16)
            : defaultArrowColor(track.id);
        segBounds.push({ a, b, colorHex: color });
    }
    if (segBounds.length === 0) return null;

    // Sample each segment; concatenate the unique points for the flow curve.
    // Sampling is DISTANCE-ADAPTIVE: the loop keeps the fine 0.25s base step
    // but subdivides any span whose chord exceeds the target spacing
    // (0.35 m). A very short move (1 keyframe pair a few metres apart) used
    // to yield only ~5 time-samples → a jagged, low-res tube, and increasing
    // the keyframe distance didn't help because resolution was tied to TIME,
    // not distance. Now longer distances get proportionally more samples and
    // short moves are subdivided to a smooth minimum.
    const SAMPLE_STEP = 0.25;      // base time step (s)
    const TARGET_SPACING = 0.35;   // max chord between samples (m)
    const MAX_SUBDIV = 8;          // per base step, bounds worst-case cost
    const allPts = [];
    const segPts = [];
    for (const seg of segBounds) {
        const pts = [];
        const pushSample = (t) => {
            // Balls sample their EFFECTIVE position (attachment-aware); players
            // and other items sample their own interpolated movement.
            const p = sampleEffectivePosition(track, t, allTracks);
            if (p) pts.push(new THREE.Vector3(p[0], (p[1] ?? 0) + 0.15, p[2]));
        };
        for (let t = seg.a; t < seg.b - 1e-6; t += SAMPLE_STEP) {
            const tNext = Math.min(t + SAMPLE_STEP, seg.b);
            pushSample(t);
            // Subdivide the step when the chord is too long for smoothness.
            const pa = sampleEffectivePosition(track, t, allTracks);
            const pb = sampleEffectivePosition(track, tNext, allTracks);
            if (pa && pb) {
                const dist = Math.hypot(pb[0] - pa[0], (pb[1] ?? 0) - (pa[1] ?? 0), pb[2] - pa[2]);
                const n = Math.min(MAX_SUBDIV, Math.ceil(dist / TARGET_SPACING) - 1);
                for (let k = 1; k <= n; k++) pushSample(t + (tNext - t) * (k / (n + 1)));
            }
        }
        pushSample(seg.b);
        const unique = pts.length > 0 ? [pts[0]] : [];
        for (let j = 1; j < pts.length; j++) {
            if (pts[j].distanceToSquared(unique[unique.length - 1]) > 0.0004) unique.push(pts[j]);
        }
        if (unique.length < 2) continue; // no movement in this segment — skip
        segPts.push({ pts: unique, colorHex: seg.colorHex });
        for (const v of unique) allPts.push(v);
    }
    if (segPts.length === 0) return null;

    const thickness = 0.12;
    const group = new THREE.Group();
    group.name = 'GraphicPath';
    group.userData.isGraphicPath = true;

    // One tube + glow per segment, each in its OWN colour.
    for (const seg of segPts) {
        const arrowMat = new THREE.MeshBasicMaterial({ color: seg.colorHex });
        const glowMat = new THREE.MeshBasicMaterial({
            color: seg.colorHex, transparent: true, opacity: 0.15, depthWrite: false,
        });
        // Dense polyline: smooth arcs, sharp corners at keyframes (no
        // CatmullRom overshoot on large direction changes).
        const curve = new PolylineCurve(seg.pts);
        const tubularSegments = Math.max(32, seg.pts.length * 8);
        group.add(new THREE.Mesh(new THREE.TubeGeometry(curve, tubularSegments, thickness, 10, false), arrowMat));
        group.add(new THREE.Mesh(new THREE.TubeGeometry(curve, tubularSegments, thickness * 2.2, 10, false), glowMat));
    }

    // Start dot (first segment's colour)
    const dot = new THREE.Mesh(
        new THREE.SphereGeometry(0.16, 16, 16),
        new THREE.MeshBasicMaterial({ color: segPts[0].colorHex })
    );
    dot.position.copy(segPts[0].pts[0]);
    group.add(dot);

    // Arrowhead at the RUN end (last segment's colour), oriented along the
    // final tangent.
    const lastSeg = segPts[segPts.length - 1];
    const endP = lastSeg.pts[lastSeg.pts.length - 1];
    const prevP = lastSeg.pts.length > 1 ? lastSeg.pts[lastSeg.pts.length - 2] : lastSeg.pts[0];
    const dir = new THREE.Vector3().subVectors(endP, prevP).normalize();
    const head = new THREE.Mesh(
        new THREE.ConeGeometry(0.32, 0.65, 16),
        new THREE.MeshBasicMaterial({ color: lastSeg.colorHex })
    );
    head.position.copy(endP);
    head.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
    group.add(head);

    // Flow particles travel the FULL run (combined polyline)
    const flowMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthWrite: false });
    const flowGeo = new THREE.SphereGeometry(thickness * 1.4, 8, 8);
    const particles = [];
    for (let i = 0; i < 3; i++) {
        const p = new THREE.Mesh(flowGeo, flowMat.clone());
        p.userData.flowOffset = i / 3;
        particles.push(p);
        group.add(p);
    }
    group.userData.flowCurve = new PolylineCurve(allPts);
    group.userData.flowParticles = particles;
    return group;
}

export default Viewport3D;
